import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { Viewer } from '@ydrive/gs-viewer';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import {
  SplatEdit,
  SplatEditRgbaBlendMode,
  SplatEditSdf,
  SplatEditSdfType,
  SplatMesh,
} from '@ydrive/sparky';

import { SceneConfig } from '../types/sceneConfig';
import { SparkViewer, usesSparkRenderer } from '../utils/SparkViewer';
import { modifiers } from '@ydrive/sparky';
import { resolveAssetUrl } from '../utils/assetUrl';
import { inferLoadingMode, inferCapabilities, inferCapabilityStates } from '../utils/sceneCapabilities';
import { fetchManifest, ManifestData, localToWGS84 } from '../utils/manifestUtils';
import { usePoiFlythrough } from '../hooks/usePoiFlythrough';
import type { ViewerAgentChatResponse } from '../types/viewerAgent';
import { getViewerSpatialContext as readViewerSpatialContext, pickWorldPointFromPointer } from '../utils/viewerSpatialUtils';
import { screenToWorldOnPlane } from '../utils/measureUtils';
import { useI18n } from '../i18n';

import PathNetworkMiniMap from './PathNetworkMiniMap';
import LabelMiniMap from './LabelMiniMap';
import POIOverlay, { POIData } from './POIOverlay';
import MeasureTool from './MeasureTool';
import SourceImages from './SourceImages';
import FlythroughTool from './FlythroughTool';
import LabelTool from './LabelTool';
import SceneLabelsLayer, { type SceneLabelItem } from './sceneInteractions/SceneLabelsLayer';
import SeatInspectionViewer from './sceneInteractions/SeatInspectionViewer';
import ScenePinsLayer, { type ScenePinsLayerHandle } from './sceneInteractions/ScenePinsLayer';
import WaypointFlythrough, { type WaypointFlythroughWaypoint } from './WaypointFlythrough';
import ViewerOrchestrationAgent from './ViewerOrchestrationAgent';
import ExtractionTool, { type ExtractionSquareData } from './ExtractionTool';
import SelectionTool from './SelectionTool';
import ElectricalNetworkOverlay from './ElectricalNetworkOverlay';
import type { ActiveContextSelection } from '../utils/viewerSpatialUtils';
import ViewerLoadingOverlay from './ViewerLoadingOverlay';
import EditSceneTool, { type PlaceableAsset } from './EditSceneTool';
import ImageUploadPanel from './ImageUploadPanel';
import type { TrellisGeneratedAsset } from '../services/trellis.service';
import { customerApi } from '../services/customerApi';
import LineOfSightOverlay from './LineOfSightOverlay';

const LOS_ASSETS: PlaceableAsset[] = [
  { id: 'los-sign', label: 'Stop Sign',   url: '/models/stopsign.glb',       icon: '🛑', targetHeight: 4.1 },
  { id: 'los-car',  label: 'Car',         url: '/models/white_suv.glb',      icon: '🚗', targetHeight: 1.74, source: 'trellis' as const },
];

const logo = '/logo.gif';

const ELECTRICAL_NETWORK_CAMERA_POSITION: [number, number, number] = [-1696.98, 58.11, 841.41];
const ELECTRICAL_NETWORK_CAMERA_LOOK_AT: [number, number, number] = [-1692.47, 58.06, 829.12];

// Verified from output_for_suruchi/redfin_ml.ply. A small horizontal inset
// keeps the camera inside populated splats instead of on the trimmed edge.
const MEDICAL_LAKE_MOVEMENT_BOUNDS = {
  minX: -2445,
  maxX: -2125,
  minY: 735,
  maxY: 900,
  minZ: 3695,
  maxZ: 3860,
};
const MEDICAL_LAKE_GROUND_Y = 736;

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

interface GpsPosition {
  latitude: number;
  longitude: number;
  altitude: number;
  heading: number;
}

interface SceneViewerProps {
  scene: SceneConfig;
  /** POIs to display — fetched and resolved by the parent page, not here. */
  pois?: POIData[];
  /** Called when the user clicks "Image View". Receives current GPS position
   *  (segmented mode only — null for single-scene). */
  onBack?: (gpsPosition: GpsPosition | null) => void;
  /** True when loaded as a popup (gausOverride). Skips nav hint panel,
   *  starts the chat minimized, and disables the auto-submit welcome message. */
  isPopupMode?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Resolve camera init for segmented mode.
 *  URL params take precedence; scene metadata is the fallback. */
function resolveSegmentedCamera(
  scene: SceneConfig,
  searchParams: URLSearchParams
) {
  const latParam = searchParams.get('lat');
  const lonParam = searchParams.get('lon');
  const altParam = searchParams.get('alt');
  const headingParam = searchParams.get('heading');

  if (latParam && lonParam) {
    return {
      latitude: parseFloat(latParam),
      longitude: parseFloat(lonParam),
      altitude: altParam ? parseFloat(altParam) : (scene.defaultAlt ?? 1.5),
      heading: headingParam ? parseFloat(headingParam) : (scene.defaultHeading ?? 0),
    };
  }

  return {
    latitude: scene.defaultLat ?? 0,
    longitude: scene.defaultLon ?? 0,
    altitude: scene.defaultAlt ?? 1.5,
    heading: scene.defaultHeading ?? 0,
  };
}

interface DisposableViewer {
  dispose?: () => void | Promise<void>;
}

interface ViewerRuntime extends DisposableViewer {
  start?: () => void;
  addSplatScene?: (path: string, options?: Record<string, unknown>) => Promise<void>;
  addSplatScenesFromManifest?: (path: string, options?: Record<string, unknown>) => Promise<void>;
  teleportTo?: (target: { position?: number[]; geoPosition?: number[] }) => Promise<void>;
  getCameraGeodeticPosition?: () => { lat: number; lon: number; alt: number; heading?: number } | null;
  isLoadingOrUnloading?: () => boolean;
  captureFrame?: (maxWidth?: number) => string;
  camera?: {
    position: {
      x: number;
      y: number;
      z: number;
    };
    lookAt?: (target: THREE.Vector3) => void;
    updateMatrixWorld?: () => void;
  };
  controls?: {
    movementSpeed?: number;
    lookSpeed?: number;
    updateLookDirection?: () => void;
  };
  maxClickToMoveDistance?: number;
  clickTransitioning?: boolean;
  transitioningCameraTarget?: boolean;
  nextCamera?: { copy: (camera: unknown) => void };
  previousCamera?: { copy: (camera: unknown) => void };
  scene?: THREE.Scene;
  threeScene?: THREE.Scene;
  splatMesh?: SplatMesh | null;
}

type FootprintPoint = [number, number];

interface VexcelStructureInfo {
  address: string;
  condition: number | null;
  material: string;
  shape: string;
  roofHeight: number | null;
  solarDetected: boolean;
  solarScore: number | null;
  vents: number;
  treeCover: number | null;
  detectedDefects: string[];
  captureDate: string | null;
}

interface VexcelPropertyInfo {
  parcelSource: string;
  structureCount: number;
  treeCover: number | null;
  hardscapePercent: number | null;
  hardscapeArea: number | null;
  poolDetected: boolean;
  poolScore: number | null;
  poolArea: number | null;
  poolDistance: number | null;
  absentElements: string[];
  captureDate: string | null;
}

interface VexcelHeightInfo {
  terrainMin: number;
  terrainMax: number;
  aboveGroundMax: number;
  aboveGroundMean: number;
  dsmYear: number;
  dtmYear: number;
}

function scaleFootprint(ring: FootprintPoint[], factor: number): FootprintPoint[] {
  const unique = ring.length > 1
    && ring[0][0] === ring[ring.length - 1][0]
    && ring[0][1] === ring[ring.length - 1][1]
    ? ring.slice(0, -1)
    : ring;
  if (unique.length === 0) return [];
  const center = unique.reduce(
    (sum, [x, z]) => ({ x: sum.x + x / unique.length, z: sum.z + z / unique.length }),
    { x: 0, z: 0 },
  );
  return unique.map(([x, z]) => [
    center.x + (x - center.x) * factor,
    center.z + (z - center.z) * factor,
  ]);
}

function pointInsideFootprint(x: number, z: number, ring: FootprintPoint[]): boolean {
  let inside = false;
  for (let current = 0, previous = ring.length - 1; current < ring.length; previous = current, current += 1) {
    const [currentX, currentZ] = ring[current];
    const [previousX, previousZ] = ring[previous];
    const crosses = (currentZ > z) !== (previousZ > z)
      && x < ((previousX - currentX) * (z - currentZ)) / (previousZ - currentZ) + currentX;
    if (crosses) inside = !inside;
  }
  return inside;
}

let viewerDisposeChain: Promise<void> = Promise.resolve();
let activeSceneViewer: DisposableViewer | null = null;

function isExpectedDisposeError(err: unknown) {
  if (!(err instanceof Error)) return false;
  return (
    err.name === 'AbortedPromiseError' ||
    err.message === 'Scene disposed' ||
    err.message === 'Viewer disposed' ||
    err.message === 'Fetch aborted' ||
    err.message === 'Worker terminate'
  );
}

async function disposeViewer(viewer: DisposableViewer | null) {
  try {
    await viewer?.dispose?.();
  } catch (err) {
    if (!isExpectedDisposeError(err)) {
      console.warn('[SceneViewer] Viewer dispose failed:', err);
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// SceneViewer
// ─────────────────────────────────────────────────────────────────────────────

export interface SceneViewerHandle {
  captureScreenshot: () => Promise<string | null>;
}

const SceneViewer = forwardRef<SceneViewerHandle, SceneViewerProps>(
  function SceneViewer({ scene, pois = [], onBack, isPopupMode = false }, ref) {
    const [searchParams] = useSearchParams();
    const { lang, t } = useI18n();
    const mode = inferLoadingMode(scene);
    const loadKey = `${scene.sceneId}:${scene.manifest ?? ''}:${scene.gaus ?? ''}`;

    // All single-scene formats (.gaus / .gm / .ply / .rad) are rendered by Spark;
    // gs-viewer is used only for segmented (manifest) scenes. SparkViewer shims
    // the raycaster (via SplatMesh.measurePoint) and click-to-move, so measure
    // and flythrough run on both paths; selection/extraction/label/waypoint/
    // source-images still rely on unshimmed gs-viewer internals and stay hidden
    // for Spark scenes.
    const isSparkScene = mode !== 'segmented' && usesSparkRenderer(scene.gaus);
    const isTerrebonneScene = ['terrebonne-segment', 'terrebonne_roads', 'terrebonne streets'].includes(scene.sceneId.toLowerCase());
    const isLafayetteOverlayScene = scene.sceneId.toLowerCase() === 'lafayette_overlay';
    const isStadiumScene = scene.sceneId.toLowerCase() === 'stadium_zemun';
    const isMedicalLakeScene = scene.sceneId.toLowerCase() === 'medical_lake';
    const isVexcelOverlayScene = isLafayetteOverlayScene || isMedicalLakeScene;
    const [pinSources, setPinSources] = useState<string[]>([]);
    const [activePinSource, setActivePinSource] = useState('');
    useEffect(() => {
      const initial = scene.pinsUrl ?? (isStadiumScene ? '/assets/stadium_zemun/stadium_seats.pins.json' : '');
      setPinSources(initial ? [initial] : []);
      setActivePinSource(initial);
    }, [scene.sceneId, scene.pinsUrl, isStadiumScene]);
    const handlePinsReady = useCallback((url: string) => {
      setPinSources(sources => sources.includes(url) ? sources : [...sources, url]);
      setActivePinSource(url);
    }, []);

    // capabilities is purely derived — no state, no fetch
    const capabilities = useMemo(
      () => inferCapabilities(scene, pois.length > 0),
      [scene, pois]
    );
    const capabilityStates = useMemo(
      () => inferCapabilityStates(scene, pois.length > 0),
      [scene, pois]
    );

    // ── Loading / error ──────────────────────────────────────────────────────
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isViewerReady, setIsViewerReady] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [loadingStatus, setLoadingStatus] = useState(() => t.viewer.preparingModel);

    // ── UI toggles ───────────────────────────────────────────────────────────
    const [showNavPanel, setShowNavPanel] = useState(false);
    const [assetsVisible, setAssetsVisible] = useState(false);
    const [measureActive, setMeasureActive] = useState(false);
    const [measureInitialPoints, setMeasureInitialPoints] = useState<[number, number, number][] | undefined>(undefined);
    const [measurementPoints, setMeasurementPoints] = useState<Array<{ x: number; y: number; z: number }>>([]);
    const [measureKey, setMeasureKey] = useState(0);
    const [sourceImagesActive, setSourceImagesActive] = useState(false);
    const [vexcelStructuresVisible, setVexcelStructuresVisible] = useState(false);
    const [vexcelPropertiesVisible, setVexcelPropertiesVisible] = useState(false);
    const [vexcelHeightVisible, setVexcelHeightVisible] = useState(false);
    const [vexcelOrthoVisible, setVexcelOrthoVisible] = useState(false);
    const [vexcelMultispectralVisible, setVexcelMultispectralVisible] = useState(false);
    const [vexcelStructureInfo, setVexcelStructureInfo] = useState<VexcelStructureInfo | null>(null);
    const [vexcelPropertyInfo, setVexcelPropertyInfo] = useState<VexcelPropertyInfo | null>(null);
    const [vexcelHeightInfo, setVexcelHeightInfo] = useState<VexcelHeightInfo | null>(null);
    const [flythroughActive, setFlythroughActive] = useState(false);
    const [flythroughKey, setFlythroughKey] = useState(0);
    const [waypointActive, setWaypointActive] = useState(false);
    const [waypointWaypoints, setWaypointWaypoints] = useState<WaypointFlythroughWaypoint[]>([]);
    const [labelToolActive, setLabelToolActive] = useState(false);
    const [extractionActive, setExtractionActive] = useState(false);
    const [selectionActive, setSelectionActive] = useState(false);
    const [electricalNetworkVisible, setElectricalNetworkVisible] = useState(false);
    const startDriveRef = useRef<(() => void) | null>(null);

    const focusCamera = useCallback((pos: [number, number, number], lookAt: [number, number, number]) => {
      const viewer = viewerRef.current as any;
      const camera = viewer?.camera as THREE.Camera | undefined;
      if (!camera) return;
      camera.position.set(...pos);
      camera.lookAt(...lookAt);
      camera.updateMatrixWorld(true);
      viewer.nextCamera?.copy?.(camera);
      viewer.previousCamera?.copy?.(camera);
      viewer.sparkControls?.pointerControls?.rotateVelocity?.set?.(0, 0, 0);
      viewer.sparkControls?.pointerControls?.moveVelocity?.set?.(0, 0, 0);
    }, []);
    const focusElectricalNetwork = useCallback(() => {
      focusCamera(ELECTRICAL_NETWORK_CAMERA_POSITION, ELECTRICAL_NETWORK_CAMERA_LOOK_AT);
    }, [focusCamera]);
    const [imageUploadActive, setImageUploadActive] = useState(false);
    const [stagedImages, setStagedImages] = useState<File[]>([]);
    const [editSceneActive, setEditSceneActive] = useState(false);
    const [editAssetLabel, setEditAssetLabel] = useState<string>();
    const [eraseModeToken, setEraseModeToken] = useState(0);
    const [agentEraseRegion, setAgentEraseRegion] = useState<{ center: [number, number, number]; radius: number } | null>(null);
    const [agentEraseBoxes, setAgentEraseBoxes] = useState<Array<{ id: string; min: THREE.Vector3; max: THREE.Vector3 }>>([]);
    const eraseBoxCounterRef = useRef(0);
    const [agentPlacement, setAgentPlacement] = useState<{ catalogueId?: string; label: string; targetBounds?: { min: THREE.Vector3; max: THREE.Vector3 }; dimensions?: { width?: number; depth?: number; length?: number }; glbUrl?: string } | null>(null);
    const [agentSelectionMode, setAgentSelectionMode] = useState<'click' | 'box' | 'lasso'>('box');
    const [awaitingPlacementClick, setAwaitingPlacementClick] = useState(false);
    const pendingPlacementAssetRef = useRef<TrellisGeneratedAsset | null>(null);
    const [losActive, setLosActive] = useState(false);
    // losEditActive: EditSceneTool open for LOS model placement (separate from losActive)
    const [losEditActive, setLosEditActive] = useState(false);
    const [losEditAssetId, setLosEditAssetId] = useState<string>('los-car');
    const [losAutoCompute, setLosAutoCompute] = useState(false);
    const losSignRootRef      = useRef<THREE.Object3D | null>(null);
    const losSignEyeRef       = useRef<THREE.Vector3 | null>(null); // exact sign-board world pos (from measurement)
    const losCarRootRef          = useRef<THREE.Object3D | null>(null);
    const losCarOwnedByEditTool  = useRef(false); // true when EditSceneTool placed it (so remount handles disposal)
    const losCRef                = useRef<THREE.Vector3 | null>(null); // point C — user test position for sign
    const [losPlacementVersion, setLosPlacementVersion] = useState(0);
    const [losCarPlaced, setLosCarPlaced] = useState(false);
    const [losCPlacing, setLosCPlacing]   = useState(false); // true while waiting for canvas click to place C
    const [losResult, setLosResult] = useState<{ visible: boolean; distance?: number } | null>(null);
    const [losResetKey, setLosResetKey] = useState(0);
    const [generatedAssets, setGeneratedAssets] = useState<PlaceableAsset[]>([]);
    const [initialGeneratedAssetId, setInitialGeneratedAssetId] = useState<string>();
    const [sceneLabels, setSceneLabels] = useState<SceneLabelItem[]>([]);
    const [mapNavTarget, setMapNavTarget] = useState<{ latitude: number; longitude: number } | null>(null);

    // ── Render quality settings (Spark scenes only) ───────────────────────────
    const [renderBudgetM, setRenderBudgetM] = useState(() => isLafayetteOverlayScene || isStadiumScene ? 20 : 10); // millions of splats
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [gradeExposure, setGradeExposure] = useState(() => isStadiumScene ? -0.15 : 0);
    const [gradeSaturation, setGradeSaturation] = useState(() => isStadiumScene ? 1.75 : isLafayetteOverlayScene ? 2 : 1);
    const gradeRef = useRef(new modifiers.ColorGrade());

    // ── Data ─────────────────────────────────────────────────────────────────
    const [pathNetwork, setPathNetwork] = useState<number[][][]>([]);
    const [manifest, setManifest] = useState<ManifestData | null>(null);
    const [cameraPosition, setCameraPosition] = useState<{ latitude: number; longitude: number } | null>(null);

    // ── Refs ─────────────────────────────────────────────────────────────────
    const viewerContainerRef = useRef<HTMLDivElement>(null);
    const viewerRef = useRef<ViewerRuntime | null>(null);
    const loadTokenRef = useRef(0);
    const readyPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const assetsBtnRef = useRef<HTMLButtonElement>(null);
    const measureBtnRef = useRef<HTMLButtonElement>(null);
    const sourceImagesBtnRef = useRef<HTMLButtonElement>(null);
    const flythroughBtnRef = useRef<HTMLButtonElement>(null);
    const labelToolBtnRef = useRef<HTMLButtonElement>(null);
    const agentExternalSubmitRef = useRef<((text: string) => void) | null>(null);
    const lastUserMessageRef = useRef<string>('');
    const pendingSelectionQuestionRef = useRef<string | null>(null);
    const originalMovementSpeedRef = useRef<number>(5);
    const originalLookSpeedRef = useRef<number>(10);
    const originalMaxClickDistRef = useRef<number>(15);
    const agentFlythroughTargetRef = useRef<{ latitude: number; longitude: number } | null>(null);
    const agentFlythroughWorldTargetRef = useRef<[number, number, number] | null>(null);
    const scenePinsLayerRef = useRef<ScenePinsLayerHandle>(null);
    const agentAutoStartFlythroughRef = useRef(false);
    const lastPlacedModelWorldPosRef = useRef<[number, number, number] | null>(null);

    const captureScreenshot = useCallback(async (): Promise<string | null> => {
      try {
        return viewerRef.current?.captureFrame?.(1280) ?? null;
      } catch {
        return null;
      }
    }, []);

    const getCameraPosition = useCallback((): { latitude: number; longitude: number; altitude: number } | null => {
      const pos = viewerRef.current?.getCameraGeodeticPosition?.();
      if (!pos) return null;
      return { latitude: pos.lat, longitude: pos.lon, altitude: pos.alt };
    }, []);

    const activeMeasurementRef = useRef<{ points: Array<{ x: number; y: number; z: number }>; mode: string; length: number | null; area: number | null } | null>(null);
    const activeContextSelectionRef = useRef<ActiveContextSelection | null>(null);

    const getViewerSpatialContext = useCallback(() => {
      const ctx = readViewerSpatialContext(viewerRef.current as any);
      const base = ctx ?? {};
      const withMeasure = activeMeasurementRef.current ? { ...base, activeMeasurement: activeMeasurementRef.current } : base;
      const withSelection = activeContextSelectionRef.current ? { ...withMeasure, activeContextSelection: activeContextSelectionRef.current } : withMeasure;
      const withTools = {
        ...withSelection,
        activeTools: {
          measurementOpen: measureActive,
          flythroughOpen: flythroughActive,
          selectionOpen: selectionActive,
          extractionOpen: extractionActive,
        },
        ...(lastPlacedModelWorldPosRef.current
          ? { lastPlacedModelWorldPos: lastPlacedModelWorldPosRef.current }
          : {}),
      };
      return withTools;
    }, [measureActive, flythroughActive, selectionActive, extractionActive]);

    useImperativeHandle(ref, () => ({ captureScreenshot }), [captureScreenshot]);

    const getCurrentSegment = useCallback(() => null, []);
    const getManifestSegmentForPoi = useCallback(() => null, []);

    const {
      flyTo,
      flyToGeoPoint,
      selectedPoiId,
      setSelectedPoiId,
    } = usePoiFlythrough({
      viewerRef,
      pois,
      pathNetwork,
      speedRefs: {
        originalMovementSpeedRef,
        originalLookSpeedRef,
        originalMaxClickDistRef,
      },
      getCurrentSegment,
      getManifestSegmentForPoi,
    });

    // ── Toolbar mutual exclusion helpers ────────────────────────────────────
    const openAssets = useCallback(() => {
      setAssetsVisible(true);
      setMeasureActive(false);
      setSourceImagesActive(false);
      setFlythroughActive(false);
      setLabelToolActive(false);
      setShowNavPanel(false);
    }, []);

    const closeAssets = useCallback(() => {
      setAssetsVisible(false);
      setSelectedPoiId(null);
    }, [setSelectedPoiId]);

    const handleMiniMapClick = useCallback((point: { latitude: number; longitude: number }) => {
      setMapNavTarget({ latitude: point.latitude, longitude: point.longitude });
      flyToGeoPoint({
        latitude: point.latitude,
        longitude: point.longitude,
        name: 'Selected point',
      });
    }, [flyToGeoPoint]);

    const handlePoiMapClick = useCallback((id: string) => {
      setAssetsVisible(true);
      flyTo(id);
    }, [flyTo]);

    const teleportToAgentCoordinates = useCallback(async (
      target: NonNullable<ViewerAgentChatResponse['data']>['teleportTarget']
    ) => {
      const viewer = viewerRef.current;
      if (!viewer || !target) return;

      try {
        if (target.world) {
          await viewer.teleportTo?.({ position: target.world });
        } else if (target.geo) {
          await viewer.teleportTo?.({ geoPosition: target.geo });
        } else {
          return;
        }

        if (target.lookAtWorld && viewer.camera?.lookAt) {
          viewer.camera.lookAt(new THREE.Vector3(...target.lookAtWorld));
          viewer.camera.updateMatrixWorld?.();
          viewer.controls?.updateLookDirection?.();
        }

        if (viewer.nextCamera && viewer.camera) viewer.nextCamera.copy(viewer.camera);
        if (viewer.previousCamera && viewer.camera) viewer.previousCamera.copy(viewer.camera);
      } catch (error) {
        console.warn('[SceneViewer] Agent coordinate teleport failed:', error);
      }
    }, []);

    const persistAgentLabels = useCallback((labels: NonNullable<ViewerAgentChatResponse['data']>['labels']) => {
      if (!labels || labels.length === 0) return;

      labels.forEach((label) => {
        customerApi.upsertViewerAgentSceneAssetLabel(scene.sceneId, {
          name: label.text,
          world: label.world,
          properties: {
            kind: 'label',
            source: 'viewer-agent',
            confidence: label.confidence,
          },
        }).catch((error) => {
          console.warn('[SceneViewer] Failed to persist agent label:', error);
        });
      });
    }, [scene.sceneId]);

    // ── LOS sign persistence ──────────────────────────────────────────────────
    const LOS_SIGN_STORAGE_KEY = `ydrive_los_sign_${scene.sceneId}`;

    type LosSignData = {
      position: { x: number; y: number; z: number };
      rotationY: number;
      scale: number;
      assetUrl: string;
      /** Exact sign-board world position from a measurement click. When present, LOS ray fires from here directly. */
      eyeWorld?: { x: number; y: number; z: number };
    };

    const LOS_CAR_STORAGE_KEY = `ydrive_los_car_${scene.sceneId}`;

    type LosCarData = {
      position: { x: number; y: number; z: number };
      rotationY: number;
      scale: number;
    };

    const saveLosCarData = useCallback((root: THREE.Object3D) => {
      const data: LosCarData = {
        position: { x: root.position.x, y: root.position.y, z: root.position.z },
        rotationY: THREE.MathUtils.radToDeg(root.rotation.y),
        scale: root.scale.x,
      };
      localStorage.setItem(LOS_CAR_STORAGE_KEY, JSON.stringify(data));
      console.info('[LOS] Car saved to localStorage:', JSON.stringify(data, null, 2));
    }, [LOS_CAR_STORAGE_KEY]);

    const saveLosSignData = useCallback((root: THREE.Object3D, eyeWorld?: THREE.Vector3) => {
      const data: LosSignData = {
        position: { x: root.position.x, y: root.position.y, z: root.position.z },
        rotationY: THREE.MathUtils.radToDeg(root.rotation.y),
        scale: root.scale.x,
        assetUrl: '/models/stopsign.glb',
        eyeWorld: eyeWorld ? { x: eyeWorld.x, y: eyeWorld.y, z: eyeWorld.z } : undefined,
      };
      localStorage.setItem(LOS_SIGN_STORAGE_KEY, JSON.stringify(data));
      console.info('[LOS] Sign saved:', data);
    }, [LOS_SIGN_STORAGE_KEY]);

    const handleLosReset = useCallback(() => {
      const viewer = viewerRef.current as any;
      const scene3: THREE.Scene | undefined = viewer?.threeScene ?? viewer?.scene;
      if (scene3) {
        if (losSignRootRef.current) {
          scene3.remove(losSignRootRef.current);
          losSignRootRef.current.traverse((child: any) => { child.dispose?.(); });
          losSignRootRef.current = null;
        }
        if (losCarRootRef.current) {
          scene3.remove(losCarRootRef.current);
          // If EditSceneTool owns the car, its remount (via losResetKey++) will dispose it.
          if (!losCarOwnedByEditTool.current) {
            losCarRootRef.current.traverse((child: any) => { child.dispose?.(); });
          }
          losCarRootRef.current = null;
          losCarOwnedByEditTool.current = false;
        }
      }
      losSignEyeRef.current = null;
      losCRef.current = null;
      setLosPlacementVersion(v => v + 1);
      setLosCarPlaced(false);
      setLosCPlacing(false);
      setLosResult(null);
      setLosAutoCompute(false);
      setLosEditActive(false);
      setLosActive(false);
      setLosResetKey(k => k + 1);
      localStorage.removeItem(LOS_SIGN_STORAGE_KEY);
      localStorage.removeItem(LOS_CAR_STORAGE_KEY);
    }, [LOS_SIGN_STORAGE_KEY, LOS_CAR_STORAGE_KEY]);


    // Place sign GLB with its bottom at measuredWorld.y, sign face at measuredWorld.y + SIGN_TARGET_HEIGHT.
    // Pass save=true only for user-initiated placements (not hardcoded defaults).
    const placeSignAtMeasuredPoint = useCallback(async (measuredWorld: THREE.Vector3, save = false, rotationY = 0) => {
      const viewer = viewerRef.current as Record<string, unknown> | null;
      const scene3 = (viewer?.threeScene ?? viewer?.scene) as THREE.Scene | undefined;
      console.log('[LOS] placeSignAtMeasuredPoint called', { hasViewer: !!viewer, hasScene: !!scene3, measuredWorld });
      if (!scene3) {
        console.error('[LOS] Cannot place sign — viewer scene is not ready');
        return;
      }
      const SIGN_URL = '/models/stopsign.glb';
      const SIGN_TARGET_HEIGHT = 4.1;

      const gltf = await new Promise<any>((resolve, reject) =>
        new GLTFLoader().load(resolveAssetUrl(SIGN_URL)!, resolve, undefined, reject)
      );

      const s2 = ((viewerRef.current as Record<string, unknown> | null)?.threeScene
                ?? (viewerRef.current as Record<string, unknown> | null)?.scene) as THREE.Scene | undefined;
      if (!s2) { console.error('[LOS] Viewer gone before GLB loaded'); return; }

      const model = gltf.scene as THREE.Group;
      const box = new THREE.Box3().setFromObject(model);
      const rawSize = box.getSize(new THREE.Vector3());
      const center  = box.getCenter(new THREE.Vector3());
      model.position.sub(center);

      const scale    = SIGN_TARGET_HEIGHT / Math.max(rawSize.y, 0.001);
      const localMinY = -rawSize.y / 2;

      if (losSignRootRef.current) s2.remove(losSignRootRef.current);

      const root = new THREE.Object3D();
      root.add(model);
      root.scale.setScalar(scale);
      root.rotation.y = rotationY;
      root.position.set(measuredWorld.x, measuredWorld.y - localMinY * scale, measuredWorld.z);

      s2.add(root);
      losSignRootRef.current = root;
      losSignEyeRef.current  = new THREE.Vector3(measuredWorld.x, measuredWorld.y + 3.5, measuredWorld.z);
      if (save) saveLosSignData(root, measuredWorld);
      setLosPlacementVersion(v => v + 1);
      console.info('[LOS] ✅ Sign (GLB) in scene | root:', root.position.x.toFixed(2), root.position.y.toFixed(2), root.position.z.toFixed(2), '| scale:', scale.toFixed(3));
    }, [saveLosSignData]);

    // On LOS activation: restore sign from localStorage, or open EditSceneTool to place it.
    useEffect(() => {
      if (!losActive || loading || error || !isSparkScene) return;
      if (losSignRootRef.current) return;
      if (losEditActive) return; // already open

      const TERREBONNE_LOS_SIGN_FALLBACK: LosSignData = {
        position: { x: -1702.9562988096986, y: 54.8675098925336, z: 837.40540042170291 },
        rotationY: 330,
        scale: 31.605866674491452,
        assetUrl: '/models/stopsign.glb',
      };
      const saved = localStorage.getItem(LOS_SIGN_STORAGE_KEY)
        ?? (isTerrebonneScene ? JSON.stringify(TERREBONNE_LOS_SIGN_FALLBACK) : null);
      if (saved) {
        const viewer = viewerRef.current as Record<string, unknown> | null;
        const scene3 = (viewer?.threeScene ?? viewer?.scene) as THREE.Scene | undefined;
        if (!scene3) return;
        let data: LosSignData;
        try { data = JSON.parse(saved); } catch { return; }
        const gloader = new GLTFLoader();
        gloader.load(resolveAssetUrl(data.assetUrl)!, gltf => {
          const v2 = viewerRef.current as Record<string, unknown> | null;
          const s2 = (v2?.threeScene ?? v2?.scene) as THREE.Scene | undefined;
          if (!s2 || losSignRootRef.current) return;
          const root = new THREE.Object3D();
          root.add(gltf.scene);
          root.position.set(data.position.x, data.position.y, data.position.z);
          root.rotation.y = THREE.MathUtils.degToRad(data.rotationY);
          root.scale.setScalar(data.scale);
          s2.add(root);
          losSignRootRef.current = root;
          losSignEyeRef.current = data.eyeWorld
            ? new THREE.Vector3(data.eyeWorld.x, data.eyeWorld.y, data.eyeWorld.z)
            : null;
          setLosPlacementVersion(v => v + 1);
        });
        return;
      }

      // No saved position — open EditSceneTool so user can place + adjust the sign.
      setLosEditAssetId('los-sign');
      setLosEditActive(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [losActive, loading, error, isSparkScene, LOS_SIGN_STORAGE_KEY, losEditActive]);

    // Auto-restore car from localStorage once the sign is in place.
    const TERREBONNE_LOS_CAR_FALLBACK: LosCarData = {
      position: { x: -1725.66916609306, y: 55.6148751219146, z: 817.3248070135475 },
      rotationY: 59.99999999999999,
      scale: 4.355476336744698,
    };
    const TERREBONNE_LOS_C = new THREE.Vector3(-1702.86, 54.56, 827.63);

    // Auto-restore car from localStorage once the sign is in place.
    // Does NOT increment losPlacementVersion — LOS computation is deferred until user clicks the button.
    useEffect(() => {
      if (!losActive || loading || error || !isSparkScene) return;
      if (!losSignRootRef.current) return;
      if (losCarRootRef.current) return;
      const saved = localStorage.getItem(LOS_CAR_STORAGE_KEY)
        ?? (isTerrebonneScene ? JSON.stringify(TERREBONNE_LOS_CAR_FALLBACK) : null);
      if (!saved) return;
      let data: LosCarData;
      try { data = JSON.parse(saved); } catch { return; }
      const viewer = viewerRef.current as Record<string, unknown> | null;
      const scene3 = (viewer?.threeScene ?? viewer?.scene) as THREE.Scene | undefined;
      if (!scene3) return;
      new GLTFLoader().load(resolveAssetUrl('/models/white_suv.glb')!, gltf => {
        const v2 = viewerRef.current as Record<string, unknown> | null;
        const s2 = (v2?.threeScene ?? v2?.scene) as THREE.Scene | undefined;
        if (!s2 || losCarRootRef.current) return;
        const model = gltf.scene as THREE.Group;
        model.traverse((child: any) => {
          if (child.isMesh) {
            const mesh = child as THREE.Mesh;
            const origMats = Array.isArray(mesh.material) ? mesh.material as THREE.Material[] : [mesh.material as THREE.Material];
            const unlitMats = origMats.map((m: THREE.Material) => {
              const pbr = m as THREE.MeshStandardMaterial;
              const unlit = new THREE.MeshBasicMaterial({ map: pbr.map ?? pbr.emissiveMap ?? null, color: new THREE.Color(0xffffff), vertexColors: pbr.vertexColors ?? false, transparent: pbr.transparent, opacity: pbr.opacity });
              unlit.depthWrite = true; unlit.depthTest = true;
              return unlit;
            });
            mesh.material = Array.isArray(mesh.material) ? unlitMats : unlitMats[0];
            mesh.renderOrder = 2;
          }
        });
        const root = new THREE.Object3D();
        root.add(model);
        root.position.set(data.position.x, data.position.y, data.position.z);
        root.rotation.y = THREE.MathUtils.degToRad(data.rotationY);
        root.scale.setScalar(data.scale);
        s2.add(root);
        losCarRootRef.current = root;
        losCarOwnedByEditTool.current = false;
        losCRef.current = TERREBONNE_LOS_C;
        setLosCarPlaced(true);
        // No losPlacementVersion increment — computation deferred to button click.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [losActive, loading, error, isSparkScene, losPlacementVersion, LOS_CAR_STORAGE_KEY]);

    // Teleport to the LOS viewpoint whenever the tool is opened on Terrebonne.
    useEffect(() => {
      if (!losActive || !isTerrebonneScene) return;
      focusCamera([-1726.17, 60.17, 808.04], [-1712.67, 61.66, 832.33]);
    }, [losActive, scene.sceneId, focusCamera]);

    // Agent-triggered car placement: load GLB at the specified world position.
    const placeLosCarAt = useCallback(async (world: [number, number, number]) => {
      const viewer = viewerRef.current;
      if (!viewer?.scene) return;
      const CAR_URL = '/models/white_suv.glb';
      const CAR_TARGET_HEIGHT = 1.74;
      const gltf = await new Promise<any>((resolve, reject) =>
        new GLTFLoader().load(resolveAssetUrl(CAR_URL)!, resolve, undefined, reject)
      );
      if (!viewerRef.current?.scene) return;
      const model = gltf.scene as THREE.Group;
      // Apply unlit materials so the car shows its texture without scene lighting.
      model.traverse((child: any) => {
        if (child.isMesh) {
          const mesh = child as THREE.Mesh;
          const origMats = Array.isArray(mesh.material) ? mesh.material as THREE.Material[] : [mesh.material as THREE.Material];
          const unlitMats = origMats.map(m => {
            const pbr = m as THREE.MeshStandardMaterial;
            const unlit = new THREE.MeshBasicMaterial({ map: pbr.map ?? pbr.emissiveMap ?? null, color: new THREE.Color(0xffffff), vertexColors: pbr.vertexColors ?? false, transparent: pbr.transparent, opacity: pbr.opacity });
            unlit.depthWrite = true; unlit.depthTest = true;
            return unlit;
          });
          mesh.material = Array.isArray(mesh.material) ? unlitMats : unlitMats[0];
          mesh.renderOrder = 2;
        }
      });
      const box = new THREE.Box3().setFromObject(model);
      const rawSize = box.getSize(new THREE.Vector3());
      const center  = box.getCenter(new THREE.Vector3());
      model.position.sub(center);
      const scale = CAR_TARGET_HEIGHT / Math.max(rawSize.y, 0.001);
      const localMinY = -rawSize.y / 2;
      const root = new THREE.Object3D();
      root.add(model);
      root.scale.setScalar(scale);
      root.position.set(world[0], world[1] - localMinY * scale, world[2]);
      (viewerRef.current.scene as THREE.Scene).add(root);
      losCarRootRef.current = root;
      losCarOwnedByEditTool.current = false;
      setLosPlacementVersion(v => v + 1);
      setLosCarPlaced(true);
      setLosAutoCompute(true);
    }, []);

    // Canvas click handler to place point C (test sign position).
    useEffect(() => {
      if (!losCPlacing) return;
      const viewer = viewerRef.current as any;

      // Freeze click-to-move so Spark doesn't teleport the camera on the same click.
      const prevMaxClick = viewer?.maxClickToMoveDistance ?? 15;
      if (viewer) viewer.maxClickToMoveDistance = 0;

      const canvas = viewer?.renderer?.domElement as HTMLCanvasElement | undefined;
      const camera = viewer?.camera as THREE.Camera | undefined;

      if (!canvas || !camera) {
        return () => { if (viewer) viewer.maxClickToMoveDistance = prevMaxClick; };
      }

      const onCanvasDown = (e: PointerEvent) => {
        e.stopPropagation();
        const signBaseY = losSignEyeRef.current
          ? losSignEyeRef.current.y - 2.5
          : (losSignRootRef.current?.position.y ?? 57.88);
        const hit = screenToWorldOnPlane(e.clientX, e.clientY, camera, canvas, signBaseY);
        if (!hit) return;
        losCRef.current = new THREE.Vector3(hit.x, signBaseY, hit.z);
        setLosCPlacing(false);
        setLosPlacementVersion(v => v + 1);
      };

      canvas.addEventListener('pointerdown', onCanvasDown, { capture: true });
      return () => {
        canvas.removeEventListener('pointerdown', onCanvasDown, { capture: true });
        if (viewer) viewer.maxClickToMoveDistance = prevMaxClick;
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [losCPlacing]);

    // Show crosshair cursor on the viewer canvas while placing point C.
    useEffect(() => {
      if (!losCPlacing) return;
      const canvas = (viewerRef.current as any)?.renderer?.domElement as HTMLCanvasElement | undefined;
      if (!canvas) return;
      const prev = canvas.style.cursor;
      canvas.style.cursor = 'crosshair';
      return () => { canvas.style.cursor = prev; };
    }, [losCPlacing]);

    // Sorts 4 corners into counterclockwise winding order (centroid-angle method).
    // SI outputs corners as [BL,BR,TL,TR] which creates a crossing (hourglass) polygon.
    // Windows are vertical planes — sort by Y (up) to split top/bottom pairs, then by Z within each pair.
    const sortCornersWindingOrder = useCallback((pts: [number, number, number][]): [number, number, number][] => {
      if (pts.length !== 4) return pts;
      const byY = [...pts].sort((a, b) => a[1] - b[1]);
      const bottom = byY.slice(0, 2).sort((a, b) => a[2] - b[2]); // lower Y pair, sort by Z (east)
      const top = byY.slice(2, 4).sort((a, b) => a[2] - b[2]);    // upper Y pair, sort by Z (east)
      return [bottom[0], bottom[1], top[1], top[0]];               // CCW: BL, BR, TR, TL
    }, []);

    const handleGeneratedAssetReady = useCallback((asset: TrellisGeneratedAsset) => {
      const placeable: PlaceableAsset = {
        id: asset.assetId,
        label: asset.label,
        url: asset.gaussianPlyUrl ?? asset.glbUrl,
        icon: '✨',
        source: 'trellis',
        format: asset.gaussianPlyUrl ? 'splat' : 'glb',
        fallbackUrl: asset.glbUrl,
      };
      setGeneratedAssets(current => [...current.filter(item => item.id !== placeable.id), placeable]);
      setInitialGeneratedAssetId(placeable.id);
      setEditSceneActive(true);
    }, []);

    const handleTrellisPlacementReady = useCallback((asset: TrellisGeneratedAsset) => {
      pendingPlacementAssetRef.current = asset;
      setAwaitingPlacementClick(true);
    }, []);

    const handleAgentDecision = useCallback((response: ViewerAgentChatResponse) => {
      const action = response.decision?.action ?? 'none';
      switch (action) {
        case 'activateMeasurement': {
          setShowNavPanel(false);
          setSelectionActive(false);
          setMeasureActive(true);
          setSourceImagesActive(false);
          setFlythroughActive(false);
          setLabelToolActive(false);
          setAssetsVisible(false);
          setSelectedPoiId(null);
          const pts = (response.data as Record<string, unknown> | undefined)?.['measureInitialPoints'];
          const rawPts = Array.isArray(pts) && pts.length > 0 ? (pts as [number, number, number][]) : undefined;
          setMeasureInitialPoints(rawPts ? sortCornersWindingOrder(rawPts) : undefined);
          setMeasureKey((k) => k + 1);
          break;
        }
        case 'activateImageView':
          setShowNavPanel(false);
          setSelectionActive(false);
          setSourceImagesActive(true);
          setMeasureActive(false);
          setFlythroughActive(false);
          setLabelToolActive(false);
          setAssetsVisible(false);
          setSelectedPoiId(null);
          break;
        case 'activateFlythrough':
          setSelectionActive(false);
          activeContextSelectionRef.current = null;
          if (response.renderAs === 'walkthrough' && response.data?.waypoints?.length) {
            setWaypointWaypoints(response.data.waypoints as WaypointFlythroughWaypoint[]);
            setWaypointActive(true);
            setFlythroughActive(false);
            setMeasureActive(false);
            setSourceImagesActive(false);
            setLabelToolActive(false);
            setAssetsVisible(false);
            setShowNavPanel(false);
            setSelectedPoiId(null);
          } else {
            agentFlythroughTargetRef.current = response.data?.target ?? null;
            agentFlythroughWorldTargetRef.current = response.data?.targetWorld ?? null;
            agentAutoStartFlythroughRef.current = !response.data?.target && !response.data?.targetWorld;
            setFlythroughKey((k) => k + 1); // force re-mount even if tool is already open
            setFlythroughActive(true);
            setWaypointActive(false);
            setMeasureActive(false);
            setSourceImagesActive(false);
            setLabelToolActive(false);
            setAssetsVisible(false);
            setShowNavPanel(false);
            setSelectedPoiId(null);
          }
          break;
        case 'activateLabelTool':
          setShowNavPanel(false);
          setSelectionActive(false);
          setMeasureActive(false);
          setSourceImagesActive(false);
          setFlythroughActive(false);
          setAssetsVisible(false);
          setLabelToolActive(true);
          setSelectedPoiId(null);
          break;
        case 'activateExtractionTool':
          setShowNavPanel(false);
          setMeasureActive(false);
          setSourceImagesActive(false);
          setFlythroughActive(false);
          setLabelToolActive(false);
          setAssetsVisible(false);
          setSelectionActive(false);
          setSelectedPoiId(null);
          activeMeasurementRef.current = null;
          activeContextSelectionRef.current = null;
          setExtractionActive(true);
          break;
        case 'activateSelectionTool':
          pendingSelectionQuestionRef.current = lastUserMessageRef.current || null;
          setAgentSelectionMode((response.data?.selectionMode as 'click' | 'box' | 'lasso') ?? 'box');
          setShowNavPanel(false);
          setMeasureActive(false);
          setExtractionActive(false);
          setSourceImagesActive(false);
          setFlythroughActive(false);
          setLabelToolActive(false);
          setAssetsVisible(false);
          setSelectedPoiId(null);
          setSelectionActive(true);
          break;
        case 'showNavigation':
          setShowNavPanel(true);
          setAssetsVisible(false);
          setLabelToolActive(false);
          break;
        case 'showPois':
          setShowNavPanel(false);
          setAssetsVisible(true);
          setMeasureActive(false);
          setSourceImagesActive(false);
          setFlythroughActive(false);
          setLabelToolActive(false);
          break;
        case 'showSceneLabels':
          console.log('[SceneViewer] showSceneLabels', response.data?.labels ?? []);
          persistAgentLabels(response.data?.labels);
          setShowNavPanel(false);
          setMeasureActive(false);
          setSourceImagesActive(false);
          setFlythroughActive(false);
          setLabelToolActive(false);
          setAssetsVisible(false);
          setSelectedPoiId(null);
          setSceneLabels((response.data?.labels ?? []).map((label, index) => ({
            id: label.id ?? `agent-label-${index}`,
            text: label.text,
            worldPos: {
              x: label.world[0],
              y: label.world[1],
              z: label.world[2],
            },
            gps: label.gps,
          })));
          break;
        case 'navigateToPoi':
          if (response.decision?.poiId) {
            setShowNavPanel(false);
            setAssetsVisible(true);
            setMeasureActive(false);
            setSourceImagesActive(false);
            setFlythroughActive(false);
            setLabelToolActive(false);
            flyTo(response.decision.poiId);
          }
          break;
        case 'navigateToPin':
          console.info('[ScenePins] Agent navigation action', { data: response.data, layerReady: !!scenePinsLayerRef.current });
          if (response.data?.pinSectionName) {
            setShowNavPanel(false);
            scenePinsLayerRef.current?.navigateTo(response.data.pinSectionName);
          }
          break;
        case 'filterPins':
          if (response.data?.pinFilters !== undefined) {
            setShowNavPanel(false);
            scenePinsLayerRef.current?.applyFilters(response.data.pinFilters);
          }
          break;
        case 'teleportToCoordinates':
          setShowNavPanel(false);
          setMeasureActive(false);
          setSourceImagesActive(false);
          setFlythroughActive(false);
          setLabelToolActive(false);
          setAssetsVisible(false);
          setSelectedPoiId(null);
          teleportToAgentCoordinates(response.data?.teleportTarget);
          // If the agent wants to show a 3D model at this location, open EditSceneTool with it pre-placed.
          if (response.data?.placeModel?.glbUrl && response.data.placeModel.targetBounds) {
            const pm = response.data.placeModel;
            setAgentPlacement({
              catalogueId: pm.catalogueId,
              label: pm.label,
              glbUrl: pm.glbUrl,
              targetBounds: {
                min: new THREE.Vector3(...pm.targetBounds.min),
                max: new THREE.Vector3(...pm.targetBounds.max),
              },
              dimensions: pm.dimensions,
            });
            setEditSceneActive(true);
          }
          break;
        case 'showVectorOverlay':
          if (scene.sceneId.toLowerCase() === 'terrebonne-segment') {
            focusElectricalNetwork();
            setElectricalNetworkVisible(true);
          } else {
            console.warn(`[SceneViewer] Ignoring Terrebonne electrical overlay for scene "${scene.sceneId}".`);
          }
          break;
        case 'activateImageUpload':
          setShowNavPanel(false);
          setMeasureActive(false);
          setExtractionActive(false);
          setSelectionActive(false);
          setSourceImagesActive(false);
          setFlythroughActive(false);
          setLabelToolActive(false);
          setImageUploadActive(true);
          break;
        case 'placeGeneratedAsset': {
          const asset = response.data?.generatedAsset;
          if (asset) handleGeneratedAssetReady(asset);
          break;
        }
        case 'activateEditTool':
          setShowNavPanel(false);
          setMeasureActive(false);
          setExtractionActive(false);
          setSelectionActive(false);
          setSourceImagesActive(false);
          setFlythroughActive(false);
          setLabelToolActive(false);
          setEditAssetLabel(response.data?.editAssetLabel);
          setEditSceneActive(true);
          break;
        case 'activateEraseMode':
          setShowNavPanel(false);
          setMeasureActive(false);
          setExtractionActive(false);
          setSelectionActive(false);
          setSourceImagesActive(false);
          setFlythroughActive(false);
          setLabelToolActive(false);
          setAgentEraseRegion(null);
          setEraseModeToken(t => t + 1);
          setEditSceneActive(true);
          break;
        case 'eraseByCoordinates': {
          const aabb = response.data?.eraseAabb;
          const rd   = response.data?.eraseRegion;
          if (aabb) {
            // Accumulate boxes — never replace, so previous erases stay masked
            const newBox = {
              id: String(++eraseBoxCounterRef.current),
              min: new THREE.Vector3(...aabb.min),
              max: new THREE.Vector3(...aabb.max),
            };
            setAgentEraseBoxes(prev => [...prev, newBox]);
            // Also set eraseRegion so the AI-selected-region banner + confirm button appear
            if (rd) setAgentEraseRegion(rd);
            setEraseModeToken(t => t + 1);
          } else if (rd) {
            setAgentEraseRegion(rd);
          } else {
            // No bbox at all — open brush directly
            setEraseModeToken(t => t + 1);
          }
          setShowNavPanel(false);
          setMeasureActive(false);
          setExtractionActive(false);
          setSelectionActive(false);
          setSourceImagesActive(false);
          setFlythroughActive(false);
          setLabelToolActive(false);
          setEditSceneActive(true);
          break;
        }
        case 'placeAndEraseModel': {
          const pm = response.data?.placeModel;
          if (pm) {
            if (pm.eraseFirst) {
              // Preserve the requested excavation volume. A small tolerance is
              // enough to catch surface splats without deleting a tall column.
              const TOLERANCE = 0.05;
              setAgentEraseBox({
                min: new THREE.Vector3(pm.targetBounds.min[0], pm.targetBounds.min[1] - TOLERANCE, pm.targetBounds.min[2]),
                max: new THREE.Vector3(pm.targetBounds.max[0], pm.targetBounds.max[1] + TOLERANCE, pm.targetBounds.max[2]),
              });
            }
            setAgentPlacement({
              catalogueId: pm.catalogueId,
              label: pm.label,
              targetBounds: {
                min: new THREE.Vector3(...pm.targetBounds.min),
                max: new THREE.Vector3(...pm.targetBounds.max),
              },
              dimensions: pm.dimensions,
              glbUrl: pm.glbUrl,
            });
          }
          setShowNavPanel(false);
          setMeasureActive(false);
          setExtractionActive(false);
          setSelectionActive(false);
          setSourceImagesActive(false);
          setFlythroughActive(false);
          setLabelToolActive(false);
          setAgentEraseRegion(null);
          setEditSceneActive(true);
          break;
        }
        case 'activateLineOfSight': {
          setLosActive(true);
          setLosAutoCompute(false);
          const carWorld = response.data?.losCarWorld;
          if (carWorld) {
            placeLosCarAt(carWorld).catch(err => console.warn('[LOS] Agent car placement failed:', err));
          } else {
            // No car position from agent — open EditSceneTool so user places car manually
            setLosEditAssetId('los-car');
            setLosEditActive(true);
          }
          break;
        }
        case 'none':
        default:
          break;
      }
    }, [flyTo, handleGeneratedAssetReady, persistAgentLabels, placeLosCarAt, setSelectedPoiId, teleportToAgentCoordinates]);

    // When awaitingPlacementClick: intercept the next canvas click, raycast to get world
    // coordinates, then auto-submit a [TRENCH_PLACEMENT] message to the agent.
    useEffect(() => {
      if (!awaitingPlacementClick) return;
      const container = viewerContainerRef.current;
      if (!container) return;

      const handleClick = (e: MouseEvent) => {
        const asset = pendingPlacementAssetRef.current;
        if (!asset) { setAwaitingPlacementClick(false); return; }
        const worldPos = pickWorldPointFromPointer(viewerRef.current, e.clientX, e.clientY);
        if (!worldPos) return;
        setAwaitingPlacementClick(false);
        pendingPlacementAssetRef.current = null;
        const msg = `[TRENCH_PLACEMENT] Center: x=${worldPos.x.toFixed(3)}, y=${worldPos.y.toFixed(3)}, z=${worldPos.z.toFixed(3)}. GLB: ${asset.glbUrl}. Let's clean this space up and put a trench there.`;
        agentExternalSubmitRef.current?.(msg);
      };

      container.addEventListener('click', handleClick);
      return () => container.removeEventListener('click', handleClick);
    }, [awaitingPlacementClick]);

    // ─────────────────────────────────────────────────────────────────────────
    // Main load effect — reruns only when scene changes
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
      let isActive = true;
      const loadToken = ++loadTokenRef.current;
      const isCurrentLoad = () => isActive && loadTokenRef.current === loadToken;

      const load = async () => {
        try {
          await viewerDisposeChain;
          if (!isCurrentLoad()) return;

          setLoading(true);
          setError(null);
          setIsViewerReady(false);
          setLoadingProgress(0);
          setLoadingStatus(t.viewer.preparingModel);
          setPathNetwork([]);
          setManifest(null);
          setCameraPosition(null);
          setSceneLabels([]);

          // Container must be mounted before we create the viewer
          if (!viewerContainerRef.current) {
            throw new Error('Viewer container not mounted');
          }

          if (mode === 'unsupported') {
            throw new Error(`Scene '${scene.sceneName}' is missing both manifest and gaus URLs`);
          }

          // Dispose any previous viewer instance
          if (viewerRef.current) {
            const previousViewer = viewerRef.current;
            viewerRef.current = null;
            if (activeSceneViewer === previousViewer) activeSceneViewer = null;
            viewerDisposeChain = viewerDisposeChain.then(() => disposeViewer(previousViewer));
            await viewerDisposeChain;
            if (!isCurrentLoad()) return;
          }

          if (activeSceneViewer) {
            const previousViewer = activeSceneViewer;
            activeSceneViewer = null;
            viewerDisposeChain = viewerDisposeChain.then(() => disposeViewer(previousViewer));
            await viewerDisposeChain;
            if (!isCurrentLoad()) return;
          }

          // ── Create viewer (rootElement for both modes) ─────────────────────
          // Single-scene mode: initialCameraPosition/LookAt go on the constructor
          // (mirroring MarketingGallery). Segmented mode uses geodetic coords
          // passed to addSplatScenesFromManifest instead.
          const isRadScene = !!scene.gaus?.split('?')[0].toLowerCase().endsWith('.rad');
          const viewerOptions: Record<string, unknown> = {
            rootElement: viewerContainerRef.current,
            sharedMemoryForWorkers: false,
            cameraFar: isRadScene ? 20000 : 100000,
            disableSplatTree: false,
            cameraFoV: scene.cameraFov ?? 60,
          };

          if (mode === 'segmented') {
            viewerOptions.maxClickToMoveDistance = 15;
          } else {
            viewerOptions.enableOptimizations = true;
            if (scene.defaultCameraPos) viewerOptions.initialCameraPosition = scene.defaultCameraPos;
            if (scene.defaultLookAt) viewerOptions.initialCameraLookAt = scene.defaultLookAt;
            // Match MarketingGallery navigation for every single-scene Spark
            // asset. Its planar ("walk") preset keeps Q/E on world Y even when
            // an aerial camera is pitched steeply downward. Passing
            // sceneType="aerial" selected Sparky's free-flight preset instead,
            // rotating Q/E with the camera and making them drift horizontally.
            // Aerial scenes still receive the faster movement speed below.
          }

          const viewer = (
            isSparkScene
              ? new SparkViewer(viewerOptions as unknown as ConstructorParameters<typeof SparkViewer>[0])
              : new Viewer(viewerOptions)
          ) as ViewerRuntime;

          // Patch: click-to-move computes a backoff equal to the camera's altitude above the
          // hit point, which sends the destination *behind* the camera for aerial/high scenes.
          // After the original runs, if the destination moved backward relative to the forward
          // direction, replace it with a forward move toward the actual hit point instead.
          const _origCheckFocal = (viewer as any).checkForFocalPointChange?.bind(viewer as any);
          if (_origCheckFocal) {
            (viewer as any).checkForFocalPointChange = function () {
              const wasTransitioning = (viewer as any).transitioningCameraTarget as boolean;
              _origCheckFocal();
              if (!wasTransitioning && (viewer as any).transitioningCameraTarget) {
                const camPos = (viewer as any).camera?.position as THREE.Vector3 | undefined;
                const nextPos = (viewer as any).nextCamera?.position as THREE.Vector3 | undefined;
                const clickPos = (viewer as any).clickPosition as THREE.Vector3 | undefined;
                if (camPos && nextPos && clickPos) {
                  const movement = nextPos.clone().sub(camPos);
                  movement.y = 0;
                  const toHit = clickPos.clone().sub(camPos);
                  toHit.y = 0;
                  if (movement.dot(toHit) < 0 && toHit.length() > 0) {
                    // Destination is behind the camera — move forward toward the hit instead
                    const maxDist = ((viewer as any).maxClickToMoveDistance as number | null) ?? 15;
                    const moveAmount = Math.min(maxDist, toHit.length() * 0.7);
                    nextPos.copy(camPos).addScaledVector(toHit.normalize(), moveAmount);
                    nextPos.y = camPos.y;
                  }
                }
              }
            };
          }

          if (!isCurrentLoad()) {
            viewerDisposeChain = viewerDisposeChain.then(() => disposeViewer(viewer));
            return;
          }

          viewerRef.current = viewer;
          activeSceneViewer = viewer;

          // ── SEGMENTED LOAD PATH ────────────────────────────────────────────
          if (mode === 'segmented') {
            if (!scene.manifest) throw new Error(`Scene '${scene.sceneName}' is missing a manifest URL`);

            const cameraInit = resolveSegmentedCamera(scene, searchParams);
            console.log('[SceneViewer] Segmented camera init:', cameraInit);

            setLoadingStatus('Loading nearby scene geometry...');

            // Fire manifest utility fetch in parallel with the splat load —
            // gs-viewer fetches the manifest internally anyway so there is no
            // reason to await it first. Manifest is only used by UI that already
            // guards on manifest !== null, so we let it settle in the background.
            fetchManifest(scene.manifest)
              .then(data => { if (isCurrentLoad()) setManifest(data); })
              .catch(err => { if (isCurrentLoad()) console.warn('[SceneViewer] Could not fetch manifest data:', err); });

            await viewer.addSplatScenesFromManifest(scene.manifest, {
              showLoadingUI: false,
              ...cameraInit,
            });

            if (!isCurrentLoad() || viewerRef.current !== viewer) return;
            viewer.start();
            if ((viewer as any).medianCameraDistance === 0) (viewer as any).medianCameraDistance = 2.0;
            setLoadingProgress(100);
            setLoading(false);

            // Poll until the viewer reports a valid geodetic position and
            // the initial segment has finished loading
            if (readyPollRef.current) clearInterval(readyPollRef.current);
            readyPollRef.current = setInterval(() => {
              if (!isCurrentLoad() || viewerRef.current !== viewer) {
                clearInterval(readyPollRef.current!);
                readyPollRef.current = null;
                return;
              }
              try {
                const pos = viewerRef.current.getCameraGeodeticPosition();
                const isValidPos =
                  pos &&
                  typeof pos.lat === 'number' &&
                  isFinite(pos.lat) &&
                  isFinite(pos.lon) &&
                  (pos.lat !== 0 || pos.lon !== 0);

                if (isValidPos && !viewerRef.current.isLoadingOrUnloading()) {
                  console.log('[SceneViewer] Viewer ready:', pos);
                  if (isCurrentLoad()) {
                    setIsViewerReady(true);
                    setShowNavPanel(true);
                  }
                  clearInterval(readyPollRef.current!);
                  readyPollRef.current = null;
                }
              } catch {
                // getCameraGeodeticPosition not available yet — keep polling
              }
            }, 500);

            // ── SINGLE-SCENE LOAD PATH ─────────────────────────────────────────
          } else {
            if (!scene.gaus) throw new Error(`Scene '${scene.sceneName}' is missing a gaus URL`);

            console.log('[SceneViewer] Single-scene camera init:', {
              initialCameraPosition: scene.defaultCameraPos,
              initialCameraLookAt: scene.defaultLookAt,
            });

            const splatBufferOptions: Record<string, unknown> = { showLoadingUI: false };
            splatBufferOptions.onProgress = (progress: number | undefined, progressLabel?: string) => {
              if (!isCurrentLoad()) return;

              if (typeof progress === 'number' && Number.isFinite(progress)) {
                const boundedProgress = Math.max(0, Math.min(100, Math.round(progress)));
                setLoadingProgress(boundedProgress);
                setLoadingStatus(t.viewer.downloadingModel(boundedProgress));
                return;
              }

              if (progressLabel) {
                setLoadingStatus(t.viewer.loadingModelLabel(progressLabel));
              }
            };
            if (scene.envMap) {
              splatBufferOptions.environmentMapPath = resolveAssetUrl(scene.envMap);
              splatBufferOptions.deferEnvironmentMap = true;
            }
            if (scene.imagePoses) {
              splatBufferOptions.imagePosesPath = resolveAssetUrl(scene.imagePoses);
              splatBufferOptions.deferImagePoses = true;
            }
            if (scene.coordinateFrame) {
              splatBufferOptions.coordinateFrame = scene.coordinateFrame;
            }

            await viewer.addSplatScene(resolveAssetUrl(scene.gaus)!, splatBufferOptions);

            if (!isCurrentLoad() || viewerRef.current !== viewer) return;
            viewer.start();
            if ((viewer as any).medianCameraDistance === 0) (viewer as any).medianCameraDistance = 2.0;

            if (isSparkScene && (scene.sceneType === 'aerial' || (!scene.sceneType && scene.sceneId === 'drone_construction_site')) && viewer.controls) {
              viewer.controls.movementSpeed = 50;
              originalMovementSpeedRef.current = 50;
            }

            setLoadingProgress(100);
            setLoading(false);
            setIsViewerReady(true);
            if (!isPopupMode) setShowNavPanel(true);
          }

        } catch (err) {
          if (!isCurrentLoad() || isExpectedDisposeError(err)) return;
          console.error('[SceneViewer] Load error:', err);
          if (isCurrentLoad()) {
            setError(err instanceof Error ? err.message : 'Failed to load scene');
            setLoading(false);
          }
        }
      };

      load();

      return () => {
        isActive = false;
        loadTokenRef.current += 1;

        if (readyPollRef.current) {
          clearInterval(readyPollRef.current);
          readyPollRef.current = null;
        }
        if (viewerRef.current) {
          const viewer = viewerRef.current;
          viewerRef.current = null;
          if (activeSceneViewer === viewer) activeSceneViewer = null;
          viewerDisposeChain = viewerDisposeChain.then(() => disposeViewer(viewer));
        }
        // Reset LOS refs so the auto-load effect re-places models in the new viewer
        // (needed for React StrictMode which unmounts and remounts the effect).
        losSignRootRef.current = null;
        losSignEyeRef.current  = null;
        losCarRootRef.current  = null;
        console.log('[LOS] viewer cleanup — LOS refs reset');
      };
    }, [loadKey]); // eslint-disable-line react-hooks/exhaustive-deps
    // sceneId plus manifest/gaus is the signal that means "load a different scene".
    // searchParams are read at call-time inside load() so they don't need to
    // be deps (they don't change while the viewer is active).

    // Clear selection context whenever the loaded scene changes
    useEffect(() => {
      setSelectionActive(false);
      activeContextSelectionRef.current = null;
    }, [loadKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─────────────────────────────────────────────────────────────────────────
    // Path network
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
      if (!scene.pathNetworkUrl) {
        setPathNetwork([]);
        return;
      }
      let cancelled = false;

      fetch(scene.pathNetworkUrl)
        .then(r => {
          if (!r.ok) throw new Error(`Path network fetch failed: ${r.status}`);
          return r.json();
        })
        .then(data => { if (!cancelled) setPathNetwork(data); })
        .catch(err => console.error('[SceneViewer] Path network error:', err));

      return () => {
        cancelled = true;
        setPathNetwork([]);
      };
    }, [scene.pathNetworkUrl]);

    // Sync render budget slider → SparkViewer (no-op for non-Spark scenes).
    useEffect(() => {
      const viewer = viewerRef.current as SparkViewer | null;
      viewer?.setLodSplatCount?.(renderBudgetM * 1_000_000);
    }, [renderBudgetM, isViewerReady]);

    useEffect(() => {
      if (!isViewerReady || !isMedicalLakeScene || !isSparkScene) return;
      const viewer = viewerRef.current as SparkViewer | null;
      if (!viewer) return;
      viewer.setMovementBounds(MEDICAL_LAKE_MOVEMENT_BOUNDS);
      viewer.fitCameraToXZBounds(MEDICAL_LAKE_MOVEMENT_BOUNDS, MEDICAL_LAKE_GROUND_Y);
      return () => viewer.setMovementBounds(null);
    }, [isMedicalLakeScene, isSparkScene, isViewerReady, loadKey]);

    // Attach ColorGrade to the SplatMesh once the scene is ready.
    useEffect(() => {
      if (!isViewerReady || !isSparkScene) return;
      const mesh = (viewerRef.current as SparkViewer | null)?.splatMesh;
      if (!mesh) return;
      gradeRef.current.attach(mesh);
      const isRad = !!scene.gaus?.split('?')[0].toLowerCase().endsWith('.rad');
      if (isRad) {
        const exposure = isStadiumScene ? -0.15 : isTerrebonneScene ? 0.50 : 0;
        const saturation = isStadiumScene ? 1.75 : isLafayetteOverlayScene ? 2 : 1.35;
        setGradeExposure(exposure);
        setGradeSaturation(saturation);
        gradeRef.current.set(mesh, { exposure, saturation });
      }
    }, [isViewerReady, isSparkScene, isLafayetteOverlayScene, isTerrebonneScene, isStadiumScene, scene.gaus]);

    // Lafayette overlay calibration: Vexcel WGS84 vertices are converted to
    // observer-relative ENU, then through the aerial reconstruction's fitted
    // ENU-to-optimized similarity transform and Spark's Three.js axis mapping.
    useEffect(() => {
      if (!isViewerReady || !vexcelStructuresVisible || scene.sceneId.toLowerCase() !== 'lafayette_overlay') return;
      const threeScene = viewerRef.current?.threeScene ?? viewerRef.current?.scene;
      if (!threeScene) return;
      const splatMesh = viewerRef.current?.splatMesh;

      const group = new THREE.Group();
      group.name = 'vexcel-lafayette-structures';
      let cancelled = false;

      fetch('/overlays/lafayette/structure-geometry.world.json')
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .then((data) => {
          if (cancelled) return;
          const groundY = Number(data.groundY) || 1543.2;
          const material = new THREE.LineBasicMaterial({ color: 0xffa928, depthTest: false, transparent: true, opacity: 0.95 });
          const features = data.features ?? [];
          const testFeature = features[0];

          // Proof of concept: replace the first footprint line with a highlight
          // painted directly onto the corresponding reconstruction splats.
          // A small footprint expansion tolerates the known provisional
          // WGS84-to-scene alignment error without reaching adjacent buildings.
          const testRing = scaleFootprint(
            (testFeature?.geometry?.coordinates?.[0]?.[0] ?? []) as FootprintPoint[],
            1.3,
          );
          if (splatMesh && testRing.length >= 3) {
            const selected: number[] = [];
            const selectedWorldPositions: number[] = [];
            const worldCenter = new THREE.Vector3();
            let minSelectedY = Number.POSITIVE_INFINITY;
            let maxSelectedY = Number.NEGATIVE_INFINITY;
            splatMesh.updateMatrixWorld(true);
            splatMesh.forEachSplat((index, localCenter) => {
              worldCenter.copy(localCenter).applyMatrix4(splatMesh.matrixWorld);
              if (pointInsideFootprint(worldCenter.x, worldCenter.z, testRing)) {
                selected.push(index);
                selectedWorldPositions.push(worldCenter.x, worldCenter.y, worldCenter.z);
                minSelectedY = Math.min(minSelectedY, worldCenter.y);
                maxSelectedY = Math.max(maxSelectedY, worldCenter.y);
              }
            });
            if (selected.length > 0) {
              // Use the same two-step path as EditSceneTool: establish the
              // index mask first, then replace its transparent erase color.
              splatMesh.setSplatMask(new Uint32Array(selected));
              const colored = splatMesh.setSplatMaskColor({
                color: 0xff6500,
                opacity: 1,
                blend: 1,
              });
              console.info(
                `[Vexcel ST splat test] Selected ${selected.length} splats for Structure feature 1; `
                + `world Y ${minSelectedY.toFixed(3)}..${maxSelectedY.toFixed(3)}; colored=${colored}`,
              );

              // RAD diagnostic: render small markers at the exact world-space
              // centers returned for the selected splat indices. These remain
              // visible even if setSplatMaskColor is unsupported for paged RAD.
              const diagnosticGeometry = new THREE.BufferGeometry();
              diagnosticGeometry.setAttribute(
                'position',
                new THREE.Float32BufferAttribute(selectedWorldPositions, 3),
              );
              const diagnosticMaterial = new THREE.PointsMaterial({
                color: 0x00ffff,
                size: 0.004,
                sizeAttenuation: true,
                depthTest: false,
                transparent: true,
                opacity: 0.9,
              });
              const diagnosticPoints = new THREE.Points(diagnosticGeometry, diagnosticMaterial);
              diagnosticPoints.name = 'vexcel-structure-splat-selection-diagnostic';
              diagnosticPoints.renderOrder = 1003;
              group.add(diagnosticPoints);
            } else {
              console.warn('[Vexcel ST splat test] No loaded splats matched Structure feature 1');
            }
          }

          // Retain the remaining footprints as visual context while omitting
          // the test feature's line so the splat result can be judged clearly.
          for (const feature of features.slice(1)) {
            for (const polygon of feature.geometry?.coordinates ?? []) {
              for (const ring of polygon) {
                const points = ring.map(([x, z]: [number, number]) => new THREE.Vector3(x, groundY, z));
                const geometry = new THREE.BufferGeometry().setFromPoints(points);
                group.add(new THREE.Line(geometry, material));
              }
            }
          }
          group.renderOrder = 1000;
          threeScene.add(group);
        })
        .catch((overlayError) => console.error('[Vexcel overlay] Failed to load structures:', overlayError));

      return () => {
        cancelled = true;
        splatMesh?.clearSplatMask();
        threeScene.remove(group);
        group.traverse((object) => {
          const renderable = object as THREE.Line | THREE.Sprite;
          if ('geometry' in renderable) renderable.geometry?.dispose?.();
          const objectMaterial = renderable.material as THREE.Material | THREE.Material[] | undefined;
          const disposeMaterial = (item: THREE.Material) => {
            const map = (item as THREE.SpriteMaterial).map;
            map?.dispose();
            item.dispose();
          };
          if (Array.isArray(objectMaterial)) objectMaterial.forEach(disposeMaterial);
          else if (objectMaterial) disposeMaterial(objectMaterial);
        });
      };
    }, [isViewerReady, scene.sceneId, vexcelStructuresVisible]);

    useEffect(() => {
      if (!isViewerReady || !vexcelPropertiesVisible || scene.sceneId.toLowerCase() !== 'lafayette_overlay') return;
      const threeScene = viewerRef.current?.threeScene ?? viewerRef.current?.scene;
      if (!threeScene) return;

      const group = new THREE.Group();
      group.name = 'vexcel-lafayette-properties';
      let cancelled = false;
      fetch('/overlays/lafayette/property-parcels.world.json')
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .then((data) => {
          if (cancelled) return;
          const groundY = Number(data.groundY) || 1543.195;
          const material = new THREE.LineBasicMaterial({ color: 0x19d3ff, depthTest: false, transparent: true, opacity: 0.9 });
          for (const feature of data.features ?? []) {
            for (const polygon of feature.geometry?.coordinates ?? []) {
              for (const ring of polygon) {
                const points = ring.map(([x, z]: [number, number]) => new THREE.Vector3(x, groundY, z));
                group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(points), material));
              }
            }
          }
          group.renderOrder = 1001;
          threeScene.add(group);
        })
        .catch((overlayError) => console.error('[Vexcel overlay] Failed to load properties:', overlayError));

      return () => {
        cancelled = true;
        threeScene.remove(group);
        group.traverse((object) => {
          const line = object as THREE.Line;
          line.geometry?.dispose?.();
          const material = line.material as THREE.Material | undefined;
          material?.dispose?.();
        });
      };
    }, [isViewerReady, scene.sceneId, vexcelPropertiesVisible]);

    // Medical Lake was reconstructed directly in its observer-relative ENU
    // frame. Its prepared Vexcel overlays therefore need no fitted scale,
    // rotation, translation, or manual visual correction.
    useEffect(() => {
      if (!isViewerReady || !vexcelStructuresVisible || !isMedicalLakeScene) return;
      const splatMesh = (viewerRef.current as SparkViewer | null)?.splatMesh;
      if (!splatMesh) return;

      let cancelled = false;
      fetch('/overlays/medical-lake/structure-geometry.world.json')
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .then((data) => {
          if (cancelled) return;
          const targetFeatures = (data.features ?? []).filter(
            (feature: { properties?: Record<string, unknown> }) => feature.properties?.['target-address'] === true,
          );
          const targetProperties = (targetFeatures[0]?.properties ?? {}) as Record<string, unknown>;
          const defectFields: Array<[string, string]> = [
            ['roof-defects.missing-material.detected', 'Missing material'],
            ['roof-defects.tarp.detected', 'Tarp'],
            ['roof-defects.discoloration.detected', 'Discoloration'],
            ['roof-defects.debris.detected', 'Debris'],
            ['roof-defects.patch.detected', 'Patch'],
            ['roof-defects.structural-damage.detected', 'Structural damage'],
            ['roof-defects.rust.detected', 'Rust'],
            ['roof-defects.discoloration.types.algae-staining.detected', 'Algae staining'],
            ['roof-defects.discoloration.types.vent-staining.detected', 'Vent staining'],
            ['roof-defects.discoloration.types.water-pooling.detected', 'Water pooling'],
          ];
          const numberOrNull = (value: unknown) => {
            const parsed = Number(value);
            return value !== null && value !== undefined && Number.isFinite(parsed) ? parsed : null;
          };
          setVexcelStructureInfo({
            address: String(targetProperties['target-address-label'] ?? '326 E Connie Ray Ave'),
            condition: numberOrNull(targetProperties['roof-characteristics.condition.precise-value']),
            material: String(targetProperties['roof-characteristics.material.class'] ?? 'Unknown'),
            shape: String(targetProperties['roof-characteristics.shape.value'] ?? 'Unknown'),
            roofHeight: numberOrNull(targetProperties['roof-characteristics.height']),
            solarDetected: targetProperties['roof-objects.solar.value'] === true,
            solarScore: numberOrNull(targetProperties['roof-objects.solar.score']),
            vents: Number(targetProperties['roof-objects.roof-vent.count']) || 0,
            treeCover: numberOrNull(targetProperties['roof-characteristics.tree-cover.percent']),
            detectedDefects: defectFields
              .filter(([field]) => targetProperties[field] === true)
              .map(([, label]) => label),
            captureDate: targetProperties['metadata.ortho-date-estimate']
              ? String(targetProperties['metadata.ortho-date-estimate']).slice(0, 10)
              : null,
          });

          const targetRing = scaleFootprint(
            (targetFeatures[0]?.geometry?.coordinates?.[0]?.[0] ?? [])
              .map(([x, , z]: [number, number, number]) => [x, z] as FootprintPoint),
            1.1,
          );
          const footprintCoordinates = (targetFeatures[0]?.geometry?.coordinates?.[0]?.[0] ?? [])
            .slice(0, -1) as [number, number, number][];
          const groundY = footprintCoordinates.reduce((sum, [, y]) => sum + y, 0)
            / footprintCoordinates.length;
          const vexcelRoofHeight = Number(targetFeatures[0]?.properties?.['roof-characteristics.height']);
          const roofHeight = Number.isFinite(vexcelRoofHeight) ? vexcelRoofHeight : 8;
          const roofY = groundY + roofHeight;
          if (splatMesh && targetRing.length >= 3 && Number.isFinite(groundY)) {
            const selected: number[] = [];
            const worldCenter = new THREE.Vector3();
            splatMesh.updateMatrixWorld(true);
            splatMesh.forEachSplat((index, localCenter) => {
              worldCenter.copy(localCenter).applyMatrix4(splatMesh.matrixWorld);
              if (
                worldCenter.y >= groundY - 1
                && worldCenter.y <= roofY + 1.25
                && pointInsideFootprint(worldCenter.x, worldCenter.z, targetRing)
              ) {
                selected.push(index);
              }
            });
            if (selected.length > 0) {
              splatMesh.setSplatMask(new Uint32Array(selected), {
                color: 0xffb05a,
                opacity: 1,
                blend: 0.68,
              });
              console.info(
                `[Vexcel Medical Lake] Colored ${selected.length} house splats orange from ground `
                + `${groundY.toFixed(3)} to Vexcel roof ${roofY.toFixed(3)} (${roofHeight.toFixed(3)} m)`,
              );
            } else {
              console.warn('[Vexcel Medical Lake] No loaded splats matched 326 E Connie Ray Ave');
            }
          }
        })
        .catch((overlayError) => console.error('[Vexcel Medical Lake] Failed to load structures:', overlayError));

      return () => {
        cancelled = true;
        setVexcelStructureInfo(null);
        splatMesh.clearSplatMask();
      };
    }, [isMedicalLakeScene, isViewerReady, vexcelStructuresVisible]);

    useEffect(() => {
      if (!isViewerReady || !vexcelPropertiesVisible || !isMedicalLakeScene) return;
      const splatMesh = (viewerRef.current as SparkViewer | null)?.splatMesh;
      if (!splatMesh) return;

      let cancelled = false;
      let parcelEdit: SplatEdit | null = null;
      let parcelBoundaryEdit: SplatEdit | null = null;
      fetch('/overlays/medical-lake/property-parcels.world.json')
        .then((response) => {
          if (!response.ok) throw new Error(`Property HTTP ${response.status}`);
          return response.json();
        })
        .then((data) => {
          if (cancelled) return;
          const parcelColor = 0x42e6c8;
          const targetFeatures = (data.features ?? []).filter(
            (feature: { properties?: Record<string, unknown> }) => feature.properties?.['target-address'] === true,
          );
          const targetProperties = (targetFeatures[0]?.properties ?? {}) as Record<string, unknown>;
          const numberOrNull = (value: unknown) => {
            const parsed = Number(value);
            return value !== null && value !== undefined && Number.isFinite(parsed) ? parsed : null;
          };
          setVexcelPropertyInfo({
            parcelSource: String(targetProperties['parcel-source'] ?? 'Unknown'),
            structureCount: Number(targetProperties['structure.count']) || 0,
            treeCover: numberOrNull(targetProperties['parcel-tree-cover.percent']),
            hardscapePercent: numberOrNull(targetProperties['hardscapes.percent']),
            hardscapeArea: numberOrNull(targetProperties['hardscapes.area']),
            poolDetected: targetProperties['pool.detected'] === true,
            poolScore: numberOrNull(targetProperties['pool.score']),
            poolArea: numberOrNull(targetProperties['pool.total-area']),
            poolDistance: numberOrNull(targetProperties['pool.types.above-ground.distance']),
            absentElements: [
              ['deck.detected', 'Deck'],
              ['vehicles.detected', 'Vehicles'],
              ['playground.detected', 'Playground'],
              ['trampoline.detected', 'Trampoline'],
              ['sport-court.detected', 'Sport court'],
              ['pool.types.hot-tub.detected', 'Hot tub'],
            ].filter(([field]) => targetProperties[field] === false).map(([, label]) => label),
            captureDate: targetProperties['metadata.ortho-date-estimate']
              ? String(targetProperties['metadata.ortho-date-estimate']).slice(0, 10)
              : null,
          });

          const parcelRing = (targetFeatures[0]?.geometry?.coordinates?.[0]?.[0] ?? [])
            .slice(0, -1) as [number, number, number][];
          if (parcelRing.length >= 4) {
            const center = parcelRing.reduce(
              (sum, [x, y, z]) => sum.add(new THREE.Vector3(x, y, z)),
              new THREE.Vector3(),
            ).multiplyScalar(1 / parcelRing.length);
            const firstEdge = new THREE.Vector2(
              parcelRing[1][0] - parcelRing[0][0],
              parcelRing[1][2] - parcelRing[0][2],
            );
            const secondEdge = new THREE.Vector2(
              parcelRing[2][0] - parcelRing[1][0],
              parcelRing[2][2] - parcelRing[1][2],
            );
            const groundSdf = new SplatEditSdf({
              type: SplatEditSdfType.BOX,
              color: new THREE.Color(parcelColor),
              opacity: 1,
              radius: 0.08,
            });
            groundSdf.position.copy(center);
            groundSdf.rotation.y = -Math.atan2(firstEdge.y, firstEdge.x);
            groundSdf.scale.set(firstEdge.length() / 2, 0.7, secondEdge.length() / 2);
            parcelEdit = new SplatEdit({
              name: 'Vexcel Medical Lake target parcel ground splats',
              rgbaBlendMode: SplatEditRgbaBlendMode.SET_RGB,
              softEdge: 0.12,
              sdfs: [groundSdf],
            });
            splatMesh.add(parcelEdit);

            const boundarySdfs = parcelRing.map((point, index) => {
              const next = parcelRing[(index + 1) % parcelRing.length];
              const edge = new THREE.Vector2(next[0] - point[0], next[2] - point[2]);
              const sdf = new SplatEditSdf({
                type: SplatEditSdfType.BOX,
                color: new THREE.Color(0x009b87),
                opacity: 1,
                radius: 0.06,
              });
              sdf.position.set(
                (point[0] + next[0]) / 2,
                (point[1] + next[1]) / 2,
                (point[2] + next[2]) / 2,
              );
              sdf.rotation.y = -Math.atan2(edge.y, edge.x);
              sdf.scale.set(edge.length() / 2 + 0.3, 1, 0.32);
              return sdf;
            });
            parcelBoundaryEdit = new SplatEdit({
              name: 'Vexcel Medical Lake target parcel boundary splats',
              rgbaBlendMode: SplatEditRgbaBlendMode.SET_RGB,
              softEdge: 0.08,
              sdfs: boundarySdfs,
            });
            splatMesh.add(parcelBoundaryEdit);
            console.info('[Vexcel Medical Lake] Applied native teal fill and ground-splat boundary to target parcel');
          }
        })
        .catch((overlayError) => console.error('[Vexcel Medical Lake] Failed to load properties:', overlayError));

      return () => {
        cancelled = true;
        setVexcelPropertyInfo(null);
        if (parcelEdit) splatMesh.remove(parcelEdit);
        if (parcelBoundaryEdit) splatMesh.remove(parcelBoundaryEdit);
      };
    }, [isMedicalLakeScene, isViewerReady, vexcelPropertiesVisible]);

    useEffect(() => {
      if (!isViewerReady || !vexcelHeightVisible || (!isLafayetteOverlayScene && !isMedicalLakeScene)) return;
      const threeScene = viewerRef.current?.threeScene ?? viewerRef.current?.scene;
      if (!threeScene) return;

      let mesh: THREE.Mesh | null = null;
      let cancelled = false;
      fetch(isMedicalLakeScene
        ? '/overlays/medical-lake/dsm-minus-dtm.world.json'
        : '/overlays/lafayette/dsm-minus-dtm.world.json')
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .then((data) => {
          if (cancelled) return;
          const width = Number(data.width);
          const height = Number(data.height);
          const groundY = Number(data.groundY);
          const verticalScale = Number(data.verticalScale);
          const heights = data.heights as number[];
          const terrainY = data.terrainY as number[] | undefined;
          const nw = data.corners.northwest as number[];
          const ne = data.corners.northeast as number[];
          const sw = data.corners.southwest as number[];
          const se = data.corners.southeast as number[];
          // Medical Lake stores world corners as [x, y, z], while the older
          // Lafayette export stores [x, z]. Read the horizontal Z consistently.
          const cornerZ = (corner: number[]) => corner.length >= 3 ? corner[2] : corner[1];
          const positions = new Float32Array(width * height * 3);
          const colors = new Float32Array(width * height * 3);
          const color = new THREE.Color();
          const heatmapStops = [
            { height: 0, color: new THREE.Color(0x2563eb) },
            { height: 2, color: new THREE.Color(0x06b6d4) },
            { height: 5, color: new THREE.Color(0x22c55e) },
            { height: 8, color: new THREE.Color(0xfde047) },
            { height: 12, color: new THREE.Color(0xf97316) },
            { height: 15, color: new THREE.Color(0xef4444) },
          ];
          for (let row = 0; row < height; row += 1) {
            const v = row / (height - 1);
            for (let column = 0; column < width; column += 1) {
              const u = column / (width - 1);
              const index = row * width + column;
              const x = (nw[0] * (1 - u) + ne[0] * u) * (1 - v) + (sw[0] * (1 - u) + se[0] * u) * v;
              const z = (cornerZ(nw) * (1 - u) + cornerZ(ne) * u) * (1 - v)
                + (cornerZ(sw) * (1 - u) + cornerZ(se) * u) * v;
              const aboveGround = Math.max(0, heights[index] ?? 0);
              const baseY = terrainY?.[index] ?? groundY;
              positions.set([x, baseY + aboveGround * verticalScale, z], index * 3);
              const upperStopIndex = heatmapStops.findIndex((stop) => aboveGround <= stop.height);
              const upperStop = heatmapStops[upperStopIndex === -1 ? heatmapStops.length - 1 : upperStopIndex];
              const lowerStop = heatmapStops[Math.max(0, (upperStopIndex === -1 ? heatmapStops.length - 1 : upperStopIndex) - 1)];
              const range = upperStop.height - lowerStop.height;
              const blend = range === 0 ? 0 : THREE.MathUtils.clamp((aboveGround - lowerStop.height) / range, 0, 1);
              color.copy(lowerStop.color).lerp(upperStop.color, blend);
              colors.set([color.r, color.g, color.b], index * 3);
            }
          }
          const indices: number[] = [];
          for (let row = 0; row < height - 1; row += 1) {
            for (let column = 0; column < width - 1; column += 1) {
              const a = row * width + column;
              const b = a + 1;
              const c = a + width;
              const d = c + 1;
              indices.push(a, c, b, b, c, d);
            }
          }
          const geometry = new THREE.BufferGeometry();
          geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
          geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
          geometry.setIndex(indices);
          mesh = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({
            vertexColors: true,
            transparent: true,
            opacity: 0.78,
            depthTest: false,
            side: THREE.DoubleSide,
          }));
          mesh.name = 'vexcel-dsm-minus-dtm-heatmap';
          mesh.renderOrder = 999;
          threeScene.add(mesh);
          if (isMedicalLakeScene && data.statistics) {
            setVexcelHeightInfo({
              terrainMin: Number(data.statistics.terrainMin),
              terrainMax: Number(data.statistics.terrainMax),
              aboveGroundMax: Number(data.statistics.aboveGroundMax),
              aboveGroundMean: Number(data.statistics.aboveGroundMean),
              dsmYear: Number(data.source?.dsm?.year),
              dtmYear: Number(data.source?.dtm?.year),
            });
          }
        })
        .catch((overlayError) => console.error('[Vexcel overlay] Failed to load DSM-DTM:', overlayError));

      return () => {
        cancelled = true;
        if (isMedicalLakeScene) setVexcelHeightInfo(null);
        if (!mesh) return;
        threeScene.remove(mesh);
        mesh.geometry.dispose();
        (mesh.material as THREE.Material).dispose();
      };
    }, [isLafayetteOverlayScene, isMedicalLakeScene, isViewerReady, vexcelHeightVisible]);

    useEffect(() => {
      if (!isViewerReady || !vexcelOrthoVisible || scene.sceneId.toLowerCase() !== 'lafayette_overlay') return;
      const threeScene = viewerRef.current?.threeScene ?? viewerRef.current?.scene;
      if (!threeScene) return;

      let mesh: THREE.Mesh | null = null;
      let texture: THREE.Texture | null = null;
      let cancelled = false;
      Promise.all([
        fetch('/overlays/lafayette/dsm-minus-dtm.world.json').then((response) => {
          if (!response.ok) throw new Error(`Ortho bounds HTTP ${response.status}`);
          return response.json();
        }),
        new THREE.TextureLoader().loadAsync('/overlays/lafayette/ortho-2025-z20.png'),
      ])
        .then(([data, loadedTexture]) => {
          if (cancelled) {
            loadedTexture.dispose();
            return;
          }
          texture = loadedTexture;
          texture.colorSpace = THREE.SRGBColorSpace;
          const nw = data.corners.northwest as [number, number];
          const ne = data.corners.northeast as [number, number];
          const sw = data.corners.southwest as [number, number];
          const se = data.corners.southeast as [number, number];
          const y = Number(data.groundY) - 0.002;
          const geometry = new THREE.BufferGeometry();
          geometry.setAttribute('position', new THREE.Float32BufferAttribute([
            nw[0], y, nw[1], ne[0], y, ne[1],
            sw[0], y, sw[1], se[0], y, se[1],
          ], 3));
          geometry.setAttribute('uv', new THREE.Float32BufferAttribute([
            0, 1, 1, 1,
            0, 0, 1, 0,
          ], 2));
          geometry.setIndex([0, 2, 1, 1, 2, 3]);
          mesh = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
            opacity: 0.72,
            depthTest: false,
            side: THREE.DoubleSide,
          }));
          mesh.name = 'vexcel-lafayette-ortho';
          mesh.renderOrder = 998;
          threeScene.add(mesh);
        })
        .catch((overlayError) => console.error('[Vexcel overlay] Failed to load Ortho:', overlayError));

      return () => {
        cancelled = true;
        if (mesh) {
          threeScene.remove(mesh);
          mesh.geometry.dispose();
          (mesh.material as THREE.Material).dispose();
        }
        texture?.dispose();
      };
    }, [isViewerReady, scene.sceneId, vexcelOrthoVisible]);

    useEffect(() => {
      if (!isViewerReady || !vexcelMultispectralVisible || scene.sceneId.toLowerCase() !== 'lafayette_overlay') return;
      const threeScene = viewerRef.current?.threeScene ?? viewerRef.current?.scene;
      if (!threeScene) return;

      let mesh: THREE.Mesh | null = null;
      let texture: THREE.Texture | null = null;
      let cancelled = false;
      Promise.all([
        fetch('/overlays/lafayette/dsm-minus-dtm.world.json').then((response) => {
          if (!response.ok) throw new Error(`Multispectral bounds HTTP ${response.status}`);
          return response.json();
        }),
        new THREE.TextureLoader().loadAsync('/overlays/lafayette/multispectral-irg-2025-z20.png'),
      ])
        .then(([data, loadedTexture]) => {
          if (cancelled) {
            loadedTexture.dispose();
            return;
          }
          texture = loadedTexture;
          texture.colorSpace = THREE.SRGBColorSpace;
          const nw = data.corners.northwest as [number, number];
          const ne = data.corners.northeast as [number, number];
          const sw = data.corners.southwest as [number, number];
          const se = data.corners.southeast as [number, number];
          const y = Number(data.groundY) - 0.001;
          const geometry = new THREE.BufferGeometry();
          geometry.setAttribute('position', new THREE.Float32BufferAttribute([
            nw[0], y, nw[1], ne[0], y, ne[1],
            sw[0], y, sw[1], se[0], y, se[1],
          ], 3));
          geometry.setAttribute('uv', new THREE.Float32BufferAttribute([
            0, 1, 1, 1,
            0, 0, 1, 0,
          ], 2));
          geometry.setIndex([0, 2, 1, 1, 2, 3]);
          mesh = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
            opacity: 0.78,
            depthTest: false,
            side: THREE.DoubleSide,
          }));
          mesh.name = 'vexcel-lafayette-multispectral-irg';
          mesh.renderOrder = 998;
          threeScene.add(mesh);
        })
        .catch((overlayError) => console.error('[Vexcel overlay] Failed to load multispectral IRG:', overlayError));

      return () => {
        cancelled = true;
        if (mesh) {
          threeScene.remove(mesh);
          mesh.geometry.dispose();
          (mesh.material as THREE.Material).dispose();
        }
        texture?.dispose();
      };
    }, [isViewerReady, scene.sceneId, vexcelMultispectralVisible]);

    // ─────────────────────────────────────────────────────────────────────────
    // Measurement mode locks click-to-move while still letting MeasureTool read
    // viewer.clickPosition after the viewer raycast runs.
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
      const viewer = viewerRef.current;
      if (!measureActive || !viewer) return;

      const previousMovementSpeed = viewer.controls?.movementSpeed ?? 5;
      const previousLookSpeed = viewer.controls?.lookSpeed ?? 10;
      const previousMaxClickDistance = viewer.maxClickToMoveDistance ?? 15;

      if (viewer.controls) {
        viewer.controls.movementSpeed = 0.3;
        viewer.controls.lookSpeed = 0;
      }

      viewer.maxClickToMoveDistance = 0;
      viewer.clickTransitioning = false;
      viewer.transitioningCameraTarget = false;
      if (viewer.nextCamera) viewer.nextCamera.copy(viewer.camera);
      if (viewer.previousCamera) viewer.previousCamera.copy(viewer.camera);

      return () => {
        const currentViewer = viewerRef.current;
        if (!currentViewer || currentViewer !== viewer) return;

        if (currentViewer.controls) {
          currentViewer.controls.movementSpeed = previousMovementSpeed;
          currentViewer.controls.lookSpeed = previousLookSpeed;
        }
        currentViewer.maxClickToMoveDistance = previousMaxClickDistance;
        currentViewer.clickTransitioning = false;
        currentViewer.transitioningCameraTarget = false;
        if (currentViewer.nextCamera) currentViewer.nextCamera.copy(currentViewer.camera);
        if (currentViewer.previousCamera) currentViewer.previousCamera.copy(currentViewer.camera);
      };
    }, [measureActive]);

    // Selection tool: freeze viewer navigation while active (same pattern as measureActive)
    useEffect(() => {
      const viewer = viewerRef.current;
      if (!selectionActive || !viewer) return;
      const prevMovement = viewer.controls?.movementSpeed ?? 5;
      const prevLook = viewer.controls?.lookSpeed ?? 10;
      const prevMaxClick = viewer.maxClickToMoveDistance ?? 15;
      if (viewer.controls) { viewer.controls.movementSpeed = 0; viewer.controls.lookSpeed = 0; }
      viewer.maxClickToMoveDistance = 0;
      viewer.clickTransitioning = false;
      viewer.transitioningCameraTarget = false;
      if (viewer.nextCamera) viewer.nextCamera.copy(viewer.camera);
      if (viewer.previousCamera) viewer.previousCamera.copy(viewer.camera);
      return () => {
        const v = viewerRef.current;
        if (!v || v !== viewer) return;
        if (v.controls) { v.controls.movementSpeed = prevMovement; v.controls.lookSpeed = prevLook; }
        v.maxClickToMoveDistance = prevMaxClick;
        v.clickTransitioning = false;
        v.transitioningCameraTarget = false;
        if (v.nextCamera) v.nextCamera.copy(v.camera);
        if (v.previousCamera) v.previousCamera.copy(v.camera);
      };
    }, [selectionActive]);

    // ─────────────────────────────────────────────────────────────────────────
    // 'H' key → reset camera to initial load position
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key !== 'h' && e.key !== 'H') return;
        const target = e.target as Element;
        if (target.closest('input, textarea, [contenteditable]')) return;
        (viewerRef.current as any)?.resetCamera?.();
      };
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    // ─────────────────────────────────────────────────────────────────────────
    // Camera position tracking → minimap dot
    // Only runs in segmented mode once manifest observer coords are available.
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
      if (mode !== 'segmented' || !manifest) return;

      const id = setInterval(() => {
        try {
          const camera = viewerRef.current?.camera;
          if (!camera) return;

          const origin = manifest.observer_coordinates;
          const wgs84 = localToWGS84(
            camera.position.z,  // local X
            camera.position.x,  // local Y
            camera.position.y,  // local Z
            origin
          );
          setCameraPosition({ latitude: wgs84.latitude, longitude: wgs84.longitude });
        } catch {
          // Camera position may be unavailable while the viewer is settling.
        }
      }, 1000);

      return () => clearInterval(id);
    }, [mode, manifest]);

    // ─────────────────────────────────────────────────────────────────────────
    // Image View / Back handler
    // ─────────────────────────────────────────────────────────────────────────
    const handleBack = useCallback(() => {
      if (!onBack) return;
      try {
        const raw = viewerRef.current?.getCameraGeodeticPosition?.();
        if (raw) {
          onBack({
            latitude: raw.lat,
            longitude: raw.lon,
            altitude: raw.alt,
            heading: raw.heading ?? 0,
          });
        } else {
          onBack(null);
        }
      } catch {
        onBack(null);
      }
    }, [onBack]);

    // ─────────────────────────────────────────────────────────────────────────
    // Error state
    // ─────────────────────────────────────────────────────────────────────────
    if (error) {
      return (
        <div className="fixed inset-0 bg-black flex items-center justify-center z-[9999]">
          <div className="text-center text-white max-w-md px-8">
            <h2 className="text-2xl font-semibold mb-4">Error Loading Scene</h2>
            <p className="text-white/70 mb-6">{error}</p>
          </div>
        </div>
      );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────────────────
    return (
      <>
        {/* ── Viewer canvas mount point ─────────────────────────────────────── */}
        <div
          ref={viewerContainerRef}
          className="fixed inset-0 w-full h-full"
          style={{ zIndex: 0, background: 'black' }}
        />

        {/* ── Loading overlay ──────────────────────────────────────────────── */}
        {loading && (
          <ViewerLoadingOverlay
            sceneName={scene.sceneName}
            description={scene.loadingDescription ?? scene.description}
            thumbnail={scene.thumbnail}
            progress={loadingProgress}
            status={loadingStatus}
          />
        )}

        {/* ── Toolbar (top-right) ──────────────────────────────────────────── */}
        {isViewerReady && !showNavPanel && (
          <div className="fixed top-4 right-4 z-[1002] flex items-start gap-3">

            {/* Text buttons: Assets + Image View */}
            {(capabilities.pois || (capabilities.imageView && onBack)) && (
              <div className="flex items-center gap-2 mt-0.5">
                {capabilities.pois && (
                  <button
                    ref={assetsBtnRef}
                    onClick={() => assetsVisible ? closeAssets() : openAssets()}
                    className="flex items-center gap-3 px-5 py-2.5 rounded-xl transition-all duration-200 shadow-lg"
                    style={{
                      background: assetsVisible ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.4)',
                      backdropFilter: 'blur(8px) saturate(140%)',
                      WebkitBackdropFilter: 'blur(8px) saturate(140%)',
                      border: assetsVisible ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.1)',
                      color: 'white', fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 21s-8-6.5-8-12a8 8 0 0 1 16 0c0 5.5-8 12-8 12z" />
                      <circle cx="12" cy="9" r="2.5" />
                    </svg>
                    <span className="text-sm">{t.viewer.assetsBtn}</span>
                  </button>
                )}
                {capabilities.imageView && onBack && (
                  <button
                    ref={sourceImagesBtnRef}
                    onClick={handleBack}
                    className="flex items-center gap-3 px-5 py-2.5 rounded-xl transition-all duration-200 shadow-lg"
                    style={{
                      background: 'rgba(0,0,0,0.4)',
                      backdropFilter: 'blur(8px) saturate(140%)',
                      WebkitBackdropFilter: 'blur(8px) saturate(140%)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: 'white', fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                    <span className="text-sm">{t.viewer.imageViewBtn}</span>
                  </button>
                )}
              </div>
            )}

            {/* Settings panel — pops out to the left of the icon strip */}
            {settingsOpen && isSparkScene && (
              <div
                className="w-64 rounded-xl shadow-2xl p-4 text-sm"
                style={{
                  background: 'rgba(10,15,25,0.92)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: 'white',
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] font-semibold text-white/40 uppercase tracking-wider">Settings</p>
                  <button
                    onClick={() => setSettingsOpen(false)}
                    style={{ color: 'rgba(255,255,255,0.4)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6, lineHeight: 1 }}
                    onMouseEnter={e => (e.currentTarget.style.color = 'white')}
                    onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
                <p className="text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-3">Render Quality</p>
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-xs text-white/70">Render budget</span>
                  <span className="text-xs font-semibold text-[#FDBA59]">{renderBudgetM.toFixed(0)}M splats</span>
                </div>
                <input
                  type="range" min={1} max={20} step={1} value={renderBudgetM}
                  onChange={e => setRenderBudgetM(Number(e.target.value))}
                  className="w-full accent-[#FDBA59]"
                />
                <div className="flex justify-between text-[10px] text-white/30 mt-0.5">
                  <span>1M</span><span>20M</span>
                </div>
                <p className="text-[10px] text-white/30 mt-3 leading-relaxed">
                  Higher values improve aerial detail. Reduce if frame rate drops.
                </p>

                <p className="text-[10px] font-semibold text-white/40 uppercase tracking-wider mt-4 mb-3">Color Grading</p>

                <div className="mb-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs text-white/70">Exposure</span>
                    <span className="text-xs font-semibold text-[#FDBA59]">{gradeExposure >= 0 ? '+' : ''}{gradeExposure.toFixed(2)}</span>
                  </div>
                  <input
                    type="range" min={-2} max={2} step={0.05} value={gradeExposure}
                    onChange={e => {
                      const val = Number(e.target.value);
                      setGradeExposure(val);
                      const mesh = (viewerRef.current as SparkViewer | null)?.splatMesh;
                      if (mesh) gradeRef.current.set(mesh, { exposure: val });
                    }}
                    className="w-full accent-[#FDBA59]"
                  />
                  <div className="flex justify-between text-[10px] text-white/30 mt-0.5">
                    <span>-2</span><span>0</span><span>+2</span>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs text-white/70">Saturation</span>
                    <span className="text-xs font-semibold text-[#FDBA59]">{gradeSaturation.toFixed(2)}</span>
                  </div>
                  <input
                    type="range" min={0} max={2} step={0.05} value={gradeSaturation}
                    onChange={e => {
                      const val = Number(e.target.value);
                      setGradeSaturation(val);
                      const mesh = (viewerRef.current as SparkViewer | null)?.splatMesh;
                      if (mesh) gradeRef.current.set(mesh, { saturation: val });
                    }}
                    className="w-full accent-[#FDBA59]"
                  />
                  <div className="flex justify-between text-[10px] text-white/30 mt-0.5">
                    <span>0</span><span>1</span><span>2</span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setGradeExposure(0);
                    setGradeSaturation(1);
                    const mesh = (viewerRef.current as SparkViewer | null)?.splatMesh;
                    if (mesh) gradeRef.current.set(mesh, { exposure: 0, saturation: 1 });
                  }}
                  className="w-full rounded-md py-1 text-[11px] text-white/40 hover:text-white/70 hover:bg-white/5 transition-colors"
                >
                  Reset grading
                </button>
              </div>
            )}

            {/* LOS panel — pops out to the left of the icon strip when LOS is active */}
            {losActive && (
              <div className="w-72 rounded-xl bg-zinc-900/80 p-4 backdrop-blur-md" style={{ color: 'white' }}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-semibold text-white/40 uppercase tracking-wider">Line of Sight</span>
                  <button onClick={() => { setLosActive(false); setLosEditActive(false); }} className="text-white/40 hover:text-white transition-colors text-xs">✕</button>
                </div>

                {/* Sign not yet placed — EditSceneTool is open, show a hint */}
                {!losSignRootRef.current && losEditActive && losEditAssetId === 'los-sign' && (
                  <div className="mb-3 flex items-center gap-2 rounded-lg px-3 py-2.5 bg-white/5 border border-white/10">
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse flex-shrink-0" />
                    <span className="text-xs text-white/70">Place the sign, adjust, then click Done</span>
                  </div>
                )}

                {/* Before result: only the check button */}
                {losCarPlaced && !losResult && (
                  <button
                    onClick={() => setLosPlacementVersion(v => v + 1)}
                    className="w-full py-2.5 rounded-lg text-xs font-semibold"
                    style={{
                      background: 'rgba(0,230,118,0.15)',
                      border: '1px solid rgba(0,230,118,0.35)',
                      color: '#00e676',
                      cursor: 'pointer',
                    }}
                  >
                    Check line of sight for the car to the sign
                  </button>
                )}

                {/* After result: full panel */}
                {losResult && (
                  <>
                    {/* Result badge */}
                    <div
                      className="mb-3 flex items-center gap-2 rounded-lg px-3 py-2.5"
                      style={{ background: losResult.visible ? 'rgba(0,230,118,0.12)' : 'rgba(255,23,68,0.12)', border: `1px solid ${losResult.visible ? 'rgba(0,230,118,0.25)' : 'rgba(255,23,68,0.25)'}` }}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: losResult.visible ? '#00e676' : '#ff1744', flexShrink: 0 }} />
                      <span className="text-sm font-bold" style={{ color: losResult.visible ? '#00e676' : '#ff1744' }}>
                        {losResult.visible
                          ? `Driver can see sign${losResult.distance !== undefined ? ` — ${losResult.distance.toFixed(1)} m away` : ''}`
                          : 'Line of sight blocked'}
                      </span>
                    </div>

                    {/* A legend */}
                    <div className="mb-3">
                      <div className="flex items-start gap-2.5">
                        <span className="w-5 h-5 rounded-full bg-white/60 flex-shrink-0 flex items-center justify-center text-[#111] text-[10px] font-bold mt-0.5">A</span>
                        <span className="text-xs text-white/40 leading-relaxed">Original sign position (static)</span>
                      </div>
                    </div>

                    {/* Hand + road legends */}
                    <div className="mb-3 space-y-2">
                      <div className="flex items-start gap-2.5">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>
                          <path d="M9 11V6a2 2 0 0 1 4 0v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                          <path d="M13 10V8a2 2 0 0 1 4 0v6a6 6 0 0 1-6 6H9a6 6 0 0 1-6-6v-1a2 2 0 0 1 4 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M9 11a2 2 0 0 1 4 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                        </svg>
                        <span className="text-xs text-white/50 leading-relaxed">Click and drag the car to test other viewpoints for line of sight</span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <svg width="36" height="10" viewBox="0 0 36 10" className="flex-shrink-0">
                          <line x1="0" y1="5" x2="36" y2="5" stroke="rgba(251,191,36,0.7)" strokeWidth="2" strokeDasharray="5 4"/>
                          <circle cx="18" cy="5" r="3" fill="#f59e0b"/>
                        </svg>
                        <span className="text-xs text-white/50 leading-relaxed">Driving direction on the road</span>
                      </div>
                    </div>

                    {/* Drive + Reset buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => { if (losCRef.current) startDriveRef.current?.(); }}
                        disabled={!losCRef.current}
                        className="flex-1 py-2 rounded-lg text-xs font-bold disabled:opacity-30 disabled:cursor-not-allowed"
                        style={{ background: losCRef.current ? '#00e676' : 'rgba(255,255,255,0.08)', color: losCRef.current ? '#000' : 'rgba(255,255,255,0.3)', cursor: losCRef.current ? 'pointer' : 'not-allowed' }}
                      >
                        Drive along road until visible
                      </button>
                      <button
                        onClick={handleLosReset}
                        className="flex-1 py-2 rounded-lg text-xs font-semibold transition-colors hover:bg-white/10"
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)', cursor: 'pointer' }}
                      >
                        Reset
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Electrical network legend panel */}
            {electricalNetworkVisible && (
              <div className="w-52 rounded-xl bg-zinc-900/80 p-4 backdrop-blur-md shadow-2xl text-sm" style={{ color: 'white' }}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] font-semibold text-white/40 uppercase tracking-wider">Electrical Network</p>
                  <button
                    onClick={() => setElectricalNetworkVisible(false)}
                    style={{ color: 'rgba(255,255,255,0.4)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6, lineHeight: 1 }}
                    onMouseEnter={e => (e.currentTarget.style.color = 'white')}
                    onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <div style={{ width: 24, height: 3, background: '#38bdf8', borderRadius: 2, flexShrink: 0 }} />
                    <span className="text-xs text-white/80">Câbles BT</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div style={{ width: 24, height: 3, background: '#f59e0b', borderRadius: 2, flexShrink: 0 }} />
                    <span className="text-xs text-white/80">Câbles HTA</span>
                  </div>
                </div>
              </div>
            )}

            {/* Vertical icon strip */}
            <div className="flex flex-col gap-1 rounded-xl bg-zinc-900/80 p-1.5 backdrop-blur-md">

              {/* Settings — Spark scenes only */}
              {isSparkScene && (
                <button
                  onClick={() => setSettingsOpen(o => !o)}
                  title="Render quality &amp; color grading"
                  className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${settingsOpen ? 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-400/40' : 'text-white/50 hover:bg-white/10 hover:text-white'}`}
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                </button>
              )}

              {/* Measure */}
              <button
                onClick={() => {
                  const next = !measureActive;
                  if (next) { setSelectionActive(false); setLosActive(false); setLosEditActive(false); }
                  setMeasureActive(next);
                }}
                title="Measure"
                className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${measureActive ? 'bg-cyan-500/20 text-cyan-300 ring-1 ring-cyan-400/40' : 'text-white/50 hover:bg-white/10 hover:text-white'}`}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 12h20" />
                  <path d="M2 12l3-3M2 12l3 3" />
                  <path d="M22 12l-3-3M22 12l-3 3" />
                  <line x1="8" y1="9" x2="8" y2="15" />
                  <line x1="12" y1="7" x2="12" y2="17" />
                  <line x1="16" y1="9" x2="16" y2="15" />
                </svg>
              </button>

              {/* Select — Terrebonne only */}
              {isTerrebonneScene && (
                <button
                  onClick={() => {
                    if (selectionActive) {
                      setSelectionActive(false);
                    } else {
                      setMeasureActive(false);
                      setExtractionActive(false);
                      setFlythroughActive(false);
                      setSourceImagesActive(false);
                      setLabelToolActive(false);
                      setLosActive(false);
                      setLosEditActive(false);
                      setSelectionActive(true);
                    }
                  }}
                  title={t.viewer.selectionBtnTitle}
                  className={`relative flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${selectionActive ? 'bg-cyan-500/20 text-cyan-300 ring-1 ring-cyan-400/40' : 'text-white/50 hover:bg-white/10 hover:text-white'}`}
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 3l7.5 18 3-7 7-3z" />
                  </svg>
                  {!selectionActive && activeContextSelectionRef.current && (
                    <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-emerald-400 border border-black/50" />
                  )}
                </button>
              )}

              {/* Electrical Network — Terrebonne only; persistent full-network overlay */}
              {scene.sceneId.toLowerCase() === 'terrebonne-segment' && (
                <button
                  onClick={() => {
                    if (!electricalNetworkVisible) focusElectricalNetwork();
                    setElectricalNetworkVisible(value => !value);
                  }}
                  title="Electrical Network"
                  aria-label="Electrical Network"
                  className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${electricalNetworkVisible ? 'bg-sky-500/20 text-sky-300 ring-1 ring-sky-400/40' : 'text-white/50 hover:bg-white/10 hover:text-white'}`}
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 18 10 6l4 12 6-12" />
                    <circle cx="4" cy="18" r="1.5" />
                    <circle cx="10" cy="6" r="1.5" />
                    <circle cx="14" cy="18" r="1.5" />
                    <circle cx="20" cy="6" r="1.5" />
                  </svg>
                </button>
              )}

              {/* Line of Sight — Terrebonne only */}
              {isTerrebonneScene && <button
                onClick={() => {
                  const next = !losActive;
                  setLosActive(next);
                  if (next) {
                    setMeasureActive(false);
                    setSelectionActive(false);
                    setLosAutoCompute(false);
                  } else {
                    setLosEditActive(false);
                  }
                }}
                title="Line of Sight"
                className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${losActive ? 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-400/40' : 'text-white/50 hover:bg-white/10 hover:text-white'}`}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <circle cx="5" cy="12" r="2" />
                  <circle cx="19" cy="12" r="2" />
                </svg>
              </button>}

              {/* Edit Scene — Terrebonne only */}
              {isSparkScene && isTerrebonneScene && (
                <button
                  onClick={() => {
                    const next = !editSceneActive;
                    setEditSceneActive(next);
                    if (next) {
                      setMeasureActive(false);
                      setSelectionActive(false);
                      setLosActive(false);
                      setLosEditActive(false);
                    }
                  }}
                  title="Edit Scene"
                  className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${editSceneActive && !losEditActive ? 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-400/40' : 'text-white/50 hover:bg-white/10 hover:text-white'}`}
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                    <line x1="12" y1="22.08" x2="12" y2="12" />
                  </svg>
                </button>
              )}

              {/* Flythrough / Spin */}
              {capabilities.flythrough && (
                <button
                  ref={flythroughBtnRef}
                  onClick={() => {
                    const next = !flythroughActive;
                    setFlythroughActive(next);
                    if (next) {
                      setFlythroughKey((k) => k + 1);
                      agentFlythroughTargetRef.current = null;
                      agentFlythroughWorldTargetRef.current = null;
                      agentAutoStartFlythroughRef.current = true;
                      setMeasureActive(false);
                      setSelectionActive(false);
                      setSourceImagesActive(false);
                      setLabelToolActive(false);
                      setExtractionActive(false);
                    }
                  }}
                  title="Spin around"
                  className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${flythroughActive ? 'bg-sky-500/20 text-sky-300 ring-1 ring-sky-400/40' : 'text-white/50 hover:bg-white/10 hover:text-white'}`}
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-3.18-6.87" />
                    <path d="M21 3v6h-6" />
                    <circle cx="12" cy="12" r="1.5" />
                  </svg>
                </button>
              )}

              {/* Reset camera */}
              <button
                onClick={() => (viewerRef.current as any)?.resetCamera?.()}
                title="Reset view (H)"
                className="flex h-10 w-10 items-center justify-center rounded-lg text-white/50 hover:bg-white/10 hover:text-white transition-colors"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              </button>

            </div>

          </div>
        )}


        {/* ── Branding (bottom-left) ───────────────────────────────────────── */}
        <div className="fixed bottom-4 left-4 z-[1002] flex flex-col items-start pointer-events-none">
          <span className="text-xs text-white/40 tracking-wide mb-1">{t.viewer.poweredBy}</span>
          <div className="flex items-center gap-2">
            <img src={logo} alt="CR3ATE.AI" className="h-8 w-8 object-contain opacity-70" />
            <h1 className="font-light text-lg text-[#0CB4E4] opacity-60 font-brand">
              cr3ate.ai
            </h1>
          </div>
        </div>

        {/* ── Minimap (bottom-right) ───────────────────────────────────────── */}
        {capabilities.minimap && pathNetwork.length > 0 && cameraPosition && (
          <div className="fixed bottom-4 right-4 z-40">
            <PathNetworkMiniMap
              pathNetwork={pathNetwork}
              currentPosition={cameraPosition}
              allPois={assetsVisible ? pois : []}
              showPOIs={assetsVisible}
              selectedPoiId={selectedPoiId}
              onPoiClick={handlePoiMapClick}
              onMapClick={handleMiniMapClick}
              mapNavTarget={mapNavTarget ?? undefined}
            />
          </div>
        )}

        {/* ── Label minimap — hidden for now, teleport UX needs more work ───── */}
        {false && sceneLabels.some((l) => l.gps) && !capabilities.minimap && (
          <div className="fixed bottom-4 right-4 z-40">
            <LabelMiniMap
              labels={sceneLabels}
              onLabelClick={(worldPos) => {
                viewerRef.current?.teleportTo?.({ position: [worldPos.x, worldPos.y, worldPos.z] });
              }}
            />
          </div>
        )}

        {isViewerReady && (
          <ViewerOrchestrationAgent
            scene={scene}
            capabilities={capabilityStates}
            pois={pois}
            disabled={loading || !!error}
            ready={isPopupMode ? false : !showNavPanel}
            startMinimized={isPopupMode}
            onDecision={handleAgentDecision}
            onPinsReady={handlePinsReady}
            onGeneratedAssetReady={handleGeneratedAssetReady}
            onTrellisPlacementReady={handleTrellisPlacementReady}
            captureScreenshot={captureScreenshot}
            getCameraPosition={getCameraPosition}
            getViewerSpatialContext={getViewerSpatialContext}
            externalSubmitRef={agentExternalSubmitRef}
            onUserMessage={(text) => { lastUserMessageRef.current = text; }}
          />
        )}

        {isViewerReady && isMedicalLakeScene && vexcelStructuresVisible && vexcelStructureInfo && (
          <aside className="pointer-events-none fixed right-4 top-[22rem] z-[1001] w-72 overflow-hidden rounded-2xl border border-orange-300/25 bg-[#11151b]/90 text-white shadow-2xl backdrop-blur-xl">
            <div className="border-b border-white/10 bg-gradient-to-r from-orange-500/25 to-amber-300/5 px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-orange-400 shadow-[0_0_12px_rgba(251,146,60,0.9)]" />
                <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-orange-200">Structure insights</span>
              </div>
              <h3 className="mt-1 text-sm font-semibold leading-tight">{vexcelStructureInfo.address}</h3>
            </div>

            <div className="grid grid-cols-2 gap-px bg-white/10">
              {[
                ['Condition', vexcelStructureInfo.condition === null ? 'N/A' : `${vexcelStructureInfo.condition.toFixed(2)} / 5`],
                ['Roof height', vexcelStructureInfo.roofHeight === null ? 'N/A' : `${vexcelStructureInfo.roofHeight.toFixed(2)} m`],
                ['Material', vexcelStructureInfo.material],
                ['Roof shape', vexcelStructureInfo.shape],
                ['Roof vents', String(vexcelStructureInfo.vents)],
                ['Tree cover', vexcelStructureInfo.treeCover === null ? 'N/A' : `${vexcelStructureInfo.treeCover}%`],
              ].map(([label, value]) => (
                <div key={label} className="bg-[#11151b]/95 px-3 py-2.5">
                  <div className="text-[9px] uppercase tracking-wider text-white/45">{label}</div>
                  <div className="mt-0.5 text-xs font-medium capitalize text-white/90">{value}</div>
                </div>
              ))}
            </div>

            <div className="space-y-2.5 px-4 py-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-white/55">Solar</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${vexcelStructureInfo.solarDetected ? 'bg-amber-400/15 text-amber-200' : 'bg-white/10 text-white/60'}`}>
                  {vexcelStructureInfo.solarDetected
                    ? `Detected${vexcelStructureInfo.solarScore === null ? '' : ` · ${Math.round(vexcelStructureInfo.solarScore * 100)}%`}`
                    : 'Not detected'}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-white/55">Roof defects</span>
                <span className={`text-right text-[10px] font-semibold ${vexcelStructureInfo.detectedDefects.length === 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                  {vexcelStructureInfo.detectedDefects.length === 0
                    ? 'None detected'
                    : vexcelStructureInfo.detectedDefects.join(', ')}
                </span>
              </div>
            </div>

            <div className="border-t border-white/10 px-4 py-2 text-[9px] tracking-wide text-white/35">
              Vexcel Structure{vexcelStructureInfo.captureDate ? ` · Captured ${vexcelStructureInfo.captureDate}` : ''}
            </div>
          </aside>
        )}

        {isViewerReady && isMedicalLakeScene && vexcelPropertiesVisible && vexcelPropertyInfo && (
          <aside className={`pointer-events-none fixed top-[22rem] z-[1001] w-72 overflow-hidden rounded-2xl border border-teal-300/25 bg-[#11151b]/90 text-white shadow-2xl backdrop-blur-xl ${vexcelStructuresVisible ? 'right-[19rem]' : 'right-4'}`}>
            <div className="border-b border-white/10 bg-gradient-to-r from-teal-500/25 to-cyan-300/5 px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-teal-300 shadow-[0_0_12px_rgba(94,234,212,0.9)]" />
                <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-teal-200">Property insights</span>
              </div>
              <h3 className="mt-1 text-sm font-semibold">Target land parcel</h3>
            </div>
            <div className="grid grid-cols-2 gap-px bg-white/10">
              {[
                ['Structures', String(vexcelPropertyInfo.structureCount)],
                ['Tree cover', vexcelPropertyInfo.treeCover === null ? 'N/A' : `${vexcelPropertyInfo.treeCover}%`],
                ['Hardscape', vexcelPropertyInfo.hardscapePercent === null ? 'N/A' : `${vexcelPropertyInfo.hardscapePercent}%`],
                ['Hardscape area', vexcelPropertyInfo.hardscapeArea === null ? 'N/A' : `${vexcelPropertyInfo.hardscapeArea.toFixed(2)} m²`],
              ].map(([label, value]) => (
                <div key={label} className="bg-[#11151b]/95 px-3 py-2.5">
                  <div className="text-[9px] uppercase tracking-wider text-white/45">{label}</div>
                  <div className="mt-0.5 text-xs font-medium text-white/90">{value}</div>
                </div>
              ))}
            </div>
            <div className="space-y-2.5 px-4 py-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-white/55">Above-ground pool</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${vexcelPropertyInfo.poolDetected ? 'bg-cyan-400/15 text-cyan-200' : 'bg-white/10 text-white/60'}`}>
                  {vexcelPropertyInfo.poolDetected
                    ? `Detected${vexcelPropertyInfo.poolScore === null ? '' : ` · ${Math.round(vexcelPropertyInfo.poolScore * 100)}%`}`
                    : 'Not detected'}
                </span>
              </div>
              {vexcelPropertyInfo.poolDetected && (
                <div className="text-[10px] text-white/55">
                  {vexcelPropertyInfo.poolArea?.toFixed(2) ?? 'N/A'} m²
                  {vexcelPropertyInfo.poolDistance === null ? '' : ` · ${vexcelPropertyInfo.poolDistance.toFixed(2)} m from structure`}
                </div>
              )}
              <div className="text-[10px] leading-relaxed text-white/40">
                Not detected: {vexcelPropertyInfo.absentElements.join(', ')}
              </div>
            </div>
            <div className="border-t border-white/10 px-4 py-2 text-[9px] tracking-wide text-white/35">
              Parcel source: {vexcelPropertyInfo.parcelSource}{vexcelPropertyInfo.captureDate ? ` · Captured ${vexcelPropertyInfo.captureDate}` : ''}
            </div>
          </aside>
        )}

        {isViewerReady && isMedicalLakeScene && vexcelHeightVisible && vexcelHeightInfo && (
          <aside className={`pointer-events-none fixed top-[22rem] z-[1001] w-72 overflow-hidden rounded-2xl border border-violet-300/25 bg-[#11151b]/90 text-white shadow-2xl backdrop-blur-xl ${vexcelStructuresVisible && vexcelPropertiesVisible ? 'right-[38rem]' : vexcelStructuresVisible || vexcelPropertiesVisible ? 'right-[19rem]' : 'right-4'}`}>
            <div className="border-b border-white/10 bg-gradient-to-r from-violet-500/25 to-fuchsia-300/5 px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-violet-300 shadow-[0_0_12px_rgba(196,181,253,0.9)]" />
                <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-violet-200">Elevation insights</span>
              </div>
              <h3 className="mt-1 text-sm font-semibold">DSM − DTM height</h3>
            </div>
            <div className="grid grid-cols-2 gap-px bg-white/10">
              {[
                ['Terrain min', `${vexcelHeightInfo.terrainMin.toFixed(2)} m`],
                ['Terrain max', `${vexcelHeightInfo.terrainMax.toFixed(2)} m`],
                ['Max object', `${vexcelHeightInfo.aboveGroundMax.toFixed(2)} m`],
                ['Mean height', `${vexcelHeightInfo.aboveGroundMean.toFixed(2)} m`],
              ].map(([label, value]) => (
                <div key={label} className="bg-[#11151b]/95 px-3 py-2.5">
                  <div className="text-[9px] uppercase tracking-wider text-white/45">{label}</div>
                  <div className="mt-0.5 text-xs font-medium text-white/90">{value}</div>
                </div>
              ))}
            </div>
            <div className="px-4 py-3 text-[10px] leading-relaxed text-white/45">
              Surface height is color-mapped above bare earth. Negative DSM−DTM differences are clamped to zero.
              <div className="mt-3">
                <div
                  className="h-2 rounded-full border border-white/15"
                  style={{ background: 'linear-gradient(90deg, #2563eb 0%, #06b6d4 13%, #22c55e 33%, #fde047 53%, #f97316 80%, #ef4444 100%)' }}
                />
                <div className="mt-1 flex justify-between text-[8px] text-white/55">
                  <span>0 m</span>
                  <span>2</span>
                  <span>5</span>
                  <span>8</span>
                  <span>12</span>
                  <span>15+ m</span>
                </div>
                <div className="mt-1 flex justify-between text-[8px] uppercase tracking-wider text-white/35">
                  <span>Ground</span>
                  <span>Tall objects</span>
                </div>
              </div>
            </div>
            <div className="border-t border-white/10 px-4 py-2 text-[9px] tracking-wide text-white/35">
              DSM {vexcelHeightInfo.dsmYear} · DTM {vexcelHeightInfo.dtmYear}
            </div>
          </aside>
        )}

        {isViewerReady && isVexcelOverlayScene && (
          <div
            className="fixed right-4 top-44 z-[1002] flex w-[52px] flex-col items-center gap-1 rounded-xl bg-zinc-900/80 p-1.5 backdrop-blur-md"
            aria-label="Vexcel overlay controls"
          >
            {[
              {
                active: vexcelStructuresVisible,
                toggle: () => setVexcelStructuresVisible((visible) => !visible),
                label: 'Structures',
                shortLabel: 'ST',
                tooltip: 'Vexcel Structures — building footprints',
                color: '#ffa928',
                background: 'rgba(180, 91, 0, 0.92)',
              },
              {
                active: vexcelPropertiesVisible,
                toggle: () => setVexcelPropertiesVisible((visible) => !visible),
                label: 'Property parcels',
                shortLabel: 'PR',
                tooltip: 'Vexcel Property — parcel boundaries',
                color: '#19d3ff',
                background: 'rgba(0, 105, 140, 0.92)',
              },
              {
                active: vexcelOrthoVisible,
                toggle: () => {
                  setVexcelOrthoVisible((visible) => !visible);
                  setVexcelMultispectralVisible(false);
                },
                label: 'True Ortho',
                shortLabel: 'OR',
                tooltip: `Vexcel True Ortho — ${isMedicalLakeScene ? '2026' : '2025'} RGB aerial image`,
                color: '#86efac',
                background: 'rgba(22, 101, 52, 0.92)',
              },
              {
                active: vexcelMultispectralVisible,
                toggle: () => {
                  setVexcelMultispectralVisible((visible) => !visible);
                  setVexcelOrthoVisible(false);
                },
                label: 'Multispectral IRG',
                shortLabel: 'MS',
                tooltip: 'Vexcel Multispectral — IRG false color; vegetation appears red',
                color: '#fb7185',
                background: 'rgba(159, 18, 57, 0.92)',
              },
              {
                active: vexcelHeightVisible,
                toggle: () => setVexcelHeightVisible((visible) => !visible),
                label: 'DSM − DTM height',
                shortLabel: 'HT',
                tooltip: 'Vexcel Height — DSM minus bare-earth DTM',
                color: '#c084fc',
                background: 'rgba(93, 45, 145, 0.92)',
              },
            ].filter((overlay) => !isMedicalLakeScene || ['Structures', 'Property parcels', 'DSM − DTM height'].includes(overlay.label)).map((overlay) => (
              <div key={overlay.label} className="group relative">
                <button
                  type="button"
                  onClick={overlay.toggle}
                  title={`${overlay.active ? 'Hide' : 'Show'} ${overlay.label}`}
                  aria-label={`${overlay.active ? 'Hide' : 'Show'} ${overlay.label}`}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border text-[10px] font-bold tracking-wide text-white shadow-lg transition-colors"
                  style={{
                    background: overlay.active ? overlay.background : 'rgba(10, 15, 25, 0.88)',
                    borderColor: overlay.active ? overlay.color : 'rgba(255,255,255,0.25)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  {overlay.shortLabel}
                </button>
                <div
                  role="tooltip"
                  className="pointer-events-none absolute right-full top-1/2 mr-3 -translate-y-1/2 whitespace-nowrap rounded-md border border-white/15 bg-[#0a0f19]/95 px-3 py-2 text-xs font-medium text-white opacity-0 shadow-xl transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
                >
                  <span style={{ color: overlay.color }}>{overlay.active ? 'Hide' : 'Show'}</span>
                  {' · '}{overlay.tooltip}
                </div>
              </div>
            ))}
          </div>
        )}

        {awaitingPlacementClick && (
          <div className="pointer-events-none fixed inset-0 z-[1002] flex items-end justify-center pb-32">
            <div className="rounded-lg border border-[#0CB4E4]/40 bg-black/70 px-4 py-2 text-sm text-[#0CB4E4] shadow-lg"
              style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
              Click the road to position the trench →
            </div>
          </div>
        )}

        {!loading && !error && (
          <SelectionTool
            viewerRef={viewerRef}
            isActive={selectionActive}
            onClose={() => setSelectionActive(false)}
            onSelectionChange={(data) => { activeContextSelectionRef.current = data; }}
            initialMode={agentSelectionMode}
            onDone={(selection) => {
              activeContextSelectionRef.current = selection;
              setSelectionActive(false);
              const question = pendingSelectionQuestionRef.current;
              pendingSelectionQuestionRef.current = null;
              const submission = question || t.viewerAgent.selectionDoneGeneric;
              agentExternalSubmitRef.current?.(submission);
            }}
          />
        )}

        <ElectricalNetworkOverlay
          viewerRef={viewerRef}
          visible={electricalNetworkVisible && scene.sceneId.toLowerCase() === 'terrebonne-segment'}
          onClose={() => setElectricalNetworkVisible(false)}
          observerCoordinates={manifest?.observer_coordinates ?? null}
        />

        <ImageUploadPanel
          isOpen={imageUploadActive}
          initialFiles={stagedImages}
          onClose={() => setImageUploadActive(false)}
          onConfirm={files => {
            setStagedImages(files);
            setImageUploadActive(false);
          }}
        />

        {stagedImages.length > 0 && !imageUploadActive && (
          <div className="fixed right-4 top-20 z-[1100] flex items-center gap-3 rounded-lg border border-white/15 bg-[#0E0D0C]/95 px-3 py-2 text-xs text-white shadow-xl">
            <span>{stagedImages.length} image{stagedImages.length === 1 ? '' : 's'} ready</span>
            <button type="button" onClick={() => setImageUploadActive(true)} className="text-[#0CB4E4] hover:underline">Review</button>
            <button type="button" onClick={() => setStagedImages([])} className="text-white/50 hover:text-white" aria-label="Clear staged images">✕</button>
          </div>
        )}

        {!loading && !error && isSparkScene && (
          <EditSceneTool
            key={losActive ? `los-edit-${losResetKey}` : 'main'}
            viewerRef={viewerRef as React.MutableRefObject<unknown>}
            isActive={editSceneActive || losEditActive}
            isAerial={scene.sceneType === 'aerial' || (!scene.sceneType && scene.sceneId === 'drone_construction_site')}
            onClose={() => {
              if (losEditActive) {
                if (losEditAssetId === 'los-sign' && losSignRootRef.current) {
                  saveLosSignData(losSignRootRef.current);
                  // C is derived from A and must be redrawn after the sign moves.
                  losCRef.current = null;
                  setLosPlacementVersion(v => v + 1);
                }
                if (losEditAssetId === 'los-car' && losCarRootRef.current) {
                  saveLosCarData(losCarRootRef.current);
                  setLosCarPlaced(true);
                  setLosPlacementVersion(v => v + 1);
                }
                setLosEditActive(false);
                // If the user dismissed sign placement without placing a sign,
                // close the whole LOS session — otherwise the placement effect
                // immediately re-opens EditSceneTool in an infinite loop.
                if (losEditAssetId === 'los-sign' && !losSignRootRef.current) {
                  setLosActive(false);
                }
              } else {
                setEditSceneActive(false);
                setAgentEraseRegion(null);
                setAgentPlacement(null);
              }
            }}
            assets={losEditActive ? LOS_ASSETS.filter(a => a.id === losEditAssetId) : generatedAssets}
            initialAssetId={losEditActive ? losEditAssetId : initialGeneratedAssetId}
            editAssetLabel={editAssetLabel}
            eraseModeToken={eraseModeToken}
            eraseRegion={agentEraseRegion ? { center: new THREE.Vector3(...agentEraseRegion.center), radius: agentEraseRegion.radius } : undefined}
            onEraseRegionDismissed={() => { setAgentEraseRegion(null); setEditSceneActive(false); }}
            eraseBoxes={agentEraseBoxes}
            agentPlacement={agentPlacement ?? undefined}
            onAgentPlacementDone={() => setAgentPlacement(null)}
            onModelPlaced={(pos) => { lastPlacedModelWorldPosRef.current = pos; }}
            onObjectPlaced={(root, assetId) => {
              if (assetId === 'los-sign') {
                losSignRootRef.current = root;
                losCRef.current = null;
                setLosPlacementVersion(v => v + 1);
              }
              if (assetId === 'los-car')  {
                losCarRootRef.current = root;
                losCarOwnedByEditTool.current = true;
                // Don't trigger LOS yet — wait for Done (onClose)
              }
            }}
          />
        )}

        {!loading && !error && (
          <ExtractionTool
            viewerRef={viewerRef}
            isActive={extractionActive}
            onClose={() => setExtractionActive(false)}
            onExtract={(data: ExtractionSquareData) => {
              setMeasurementPoints(data.corners);
              activeMeasurementRef.current = {
                points: data.corners,
                mode: 'polygon',
                length: data.perimeter,
                area: data.area,
              };
              setExtractionActive(false);
              agentExternalSubmitRef.current?.('Extract the Gaussian splats within my selected region');
            }}
          />
        )}

        {!loading && !error && measureActive && (
          <MeasureTool
            key={measureKey}
            viewerRef={viewerRef}
            isActive={measureActive}
            // Keep the last measured line available to LOS after the measure
            // panel closes; LOS uses it as the optional drive path.
            onClose={() => { setMeasureActive(false); setMeasureInitialPoints(undefined); activeMeasurementRef.current = null; }}
            onPointsChange={(data) => { activeMeasurementRef.current = data.points.length > 0 ? data : null; setMeasurementPoints(data.points); }}
            anchorRef={measureBtnRef}
            initialPoints={measureInitialPoints?.map(([x, y, z]) => ({ x, y, z }))}
          />
        )}

        {!loading && !error && !isSparkScene && sourceImagesActive && (
          <SourceImages
            viewerRef={viewerRef}
            manifest={manifest}
            sceneId={scene.sceneId}
            isActive={sourceImagesActive}
            onClose={() => setSourceImagesActive(false)}
            anchorRef={sourceImagesBtnRef}
          />
        )}

        {!loading && !error && flythroughActive && capabilities.flythrough && (
          <FlythroughTool
            key={flythroughKey}
            viewerRef={viewerRef}
            isActive={flythroughActive}
            onClose={() => {
              setFlythroughActive(false);
              agentAutoStartFlythroughRef.current = false;
              agentFlythroughWorldTargetRef.current = null;
            }}
            anchorRef={flythroughBtnRef}
            initialGeoTarget={agentFlythroughTargetRef.current ?? undefined}
            initialWorldTarget={agentFlythroughWorldTargetRef.current ?? undefined}
            autoStartFromCamera={agentAutoStartFlythroughRef.current}
          />
        )}

        {!loading && !error && waypointActive && waypointWaypoints.length >= 2 && (
          <WaypointFlythrough
            viewerRef={viewerRef as React.MutableRefObject<any>}
            isActive={waypointActive}
            waypoints={waypointWaypoints}
            worldCoords={true}
            onClose={() => setWaypointActive(false)}
          />
        )}

        {!loading && !error && labelToolActive && (
          <LabelTool
            sceneId={scene.sceneId}
            viewerRef={viewerRef}
            isActive={labelToolActive}
            onClose={() => setLabelToolActive(false)}
            onLabelsChange={setSceneLabels}
            anchorRef={labelToolBtnRef}
          />
        )}

        {/* ── LOS: "Set as Sign A" bridge button — visible when measure tool + LOS are both open ── */}
        {losActive && measureActive && (
          <div className="fixed z-[1002]" style={{ bottom: 140, left: '50%', transform: 'translateX(-50%)' }}>
            <button
              style={{
                background: 'rgba(10,15,25,0.92)', backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255,183,0,0.5)', borderRadius: 16,
                color: '#FDBA59', fontWeight: 700, fontSize: 13,
                padding: '8px 20px', cursor: 'pointer', boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
              }}
              onClick={() => {
                const pt = activeMeasurementRef.current?.points?.[0];
                if (!pt) { alert('Click a point with the measure tool first'); return; }
                placeSignAtMeasuredPoint(new THREE.Vector3(pt.x, pt.y, pt.z), true)
                  .catch(err => console.error('[LOS] placeSignAtMeasuredPoint failed:', err));
              }}
            >
              Set measured point as Sign A
            </button>
          </div>
        )}

        {/* ── Line of Sight overlay (SVG + drag handles only) ─────────────── */}
        {!loading && !error && losActive && (
          <LineOfSightOverlay
            viewerRef={viewerRef}
            signRootRef={losSignRootRef}
            signEyeRef={losSignEyeRef}
            carRootRef={losCarRootRef}
            cPointRef={losCRef}
            placementVersion={losPlacementVersion}
            autoCompute={losAutoCompute}
            drivePath={measurementPoints}
            startDriveRef={startDriveRef}
            onLosResult={setLosResult}
          />
        )}

        {/* ── POI Overlay — single selected pin ───────────────────────────── */}
        {capabilities.pois && assetsVisible && selectedPoiId && (
          <POIOverlay viewerRef={viewerRef} pois={pois} />
        )}

        {isViewerReady && isSparkScene && activePinSource && (
          (scene.inspectionUrl || isStadiumScene) ? <SeatInspectionViewer layerRef={scenePinsLayerRef} key={scene.sceneId} sceneId={scene.sceneId}
            inspectionUrl={scene.inspectionUrl ?? '/assets/stadium_zemun/stadium.inspection.json'}
            viewerRef={viewerRef} sources={pinSources} source={activePinSource} onSourceChange={setActivePinSource}
            interactive={!labelToolActive && !measureActive && !selectionActive && !extractionActive && !flythroughActive} /> :
          <ScenePinsLayer ref={scenePinsLayerRef} key={scene.sceneId} viewerRef={viewerRef} sources={pinSources}
            source={activePinSource} onSourceChange={setActivePinSource}
            interactive={!labelToolActive && !measureActive && !selectionActive && !extractionActive && !flythroughActive} />
        )}

        {!labelToolActive && sceneLabels.length > 0 && (
          <SceneLabelsLayer
            viewerRef={viewerRef}
            labels={sceneLabels}
            maxDistance={capabilities.hasGeoAlignment ? Infinity : 40}
            showHiddenSummary
            formatHiddenSummary={(count, lastText) =>
              count === 1 ? t.sceneLabels.hiddenOne(lastText) : t.sceneLabels.hiddenMany(count)
            }
          />
        )}

        {/* ── Navigation hint panel (shown on first load) ──────────────────── */}
        {showNavPanel && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1001]"
            onClick={() => { if (isViewerReady) setShowNavPanel(false); }}
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <div
              className="relative w-full max-w-[620px] mx-6 p-8 md:p-10 rounded-[28px] text-white"
              style={{
                background: 'rgba(255,255,255,0.08)',
                backdropFilter: 'blur(4px) saturate(120%)',
                WebkitBackdropFilter: 'blur(4px) saturate(120%)',
                border: '1px solid rgba(255,255,255,0.22)',
                boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
              }}
              onClick={e => { if (isViewerReady) { e.stopPropagation(); setShowNavPanel(false); } }}
            >
              <h1 className="text-2xl text-center mb-10 tracking-widest opacity-90" style={{ fontWeight: 300 }}>
                {t.viewer.navTitle}
              </h1>

              <div className="flex flex-col md:flex-row items-stretch gap-10">
                {/* Look around */}
                <div className="flex-1 flex flex-col items-center">
                  <h2 className="text-lg font-normal mb-8 opacity-80 tracking-wide">{t.viewer.navLookAround}</h2>
                  <div className="relative w-40 h-40 flex items-center justify-center mb-8">
                    <div style={{ width: 56, height: 90, border: '1.5px solid rgba(255,255,255,0.4)', borderRadius: 26, position: 'relative', zIndex: 2 }}>
                      <div style={{ width: 4, height: 8, background: 'white', borderRadius: 1.5, position: 'absolute', top: 18, left: '50%', transform: 'translateX(-50%)', opacity: 0.9 }} />
                    </div>
                    <div style={{ position: 'absolute', width: 120, height: 120, border: '1.2px solid rgba(255,255,255,0.25)', borderLeftColor: 'transparent', borderBottomColor: 'transparent', borderRadius: '50%', top: '50%', left: '50%', transform: 'translate(-50%,-50%) rotate(-25deg)', zIndex: 1 }} />
                  </div>
                  <p className="text-[12px] uppercase tracking-[0.25em] font-light text-white/40">{t.viewer.navLookHint}</p>
                </div>

                <div className="hidden md:block w-px" style={{ background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.2), transparent)' }} />

                {/* Move */}
                <div className="flex-1 flex flex-col items-center">
                  <h2 className="text-lg font-normal mb-8 opacity-80 tracking-wide">{t.viewer.navMove}</h2>
                  <div className="grid grid-cols-3 gap-3 mb-8">
                    <div />
                    <div className="w-[52px] h-[52px] rounded-xl flex items-center justify-center border" style={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.15)' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5"><path d="m18 15-6-6-6 6" /></svg>
                    </div>
                    <div />
                    <div className="w-[52px] h-[52px] rounded-xl flex items-center justify-center border" style={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.15)' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5"><path d="m15 18-6-6 6-6" /></svg>
                    </div>
                    <div className="w-[52px] h-[52px] rounded-xl flex items-center justify-center border" style={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.15)' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5"><path d="m6 9 6 6 6-6" /></svg>
                    </div>
                    <div className="w-[52px] h-[52px] rounded-xl flex items-center justify-center border" style={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.15)' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5"><path d="m9 18 6-6-6-6" /></svg>
                    </div>
                  </div>
                  <p className="text-[12px] uppercase tracking-[0.25em] font-light text-white/40">{t.viewer.navMoveHint}</p>
                </div>
              </div>

              <div className="mt-12 text-center text-[13px] font-light text-white/30 tracking-widest">
                {t.viewer.navAdvanced} <span className="text-white/60 font-normal">WASD</span> + <span className="text-white/60 font-normal">Q</span> ({t.viewer.navUp}) / <span className="text-white/60 font-normal">E</span> ({t.viewer.navDown})
              </div>
            </div>
          </div>
        )}
      </>
    );
  });

export default SceneViewer;
